// Feature: docs/features/ionm-frontend-list-support.md
// Feature: docs/features/ionm-modern-preliminary-flagging.md
// Feature: docs/features/pdf-template-production-v1.md
// Feature: docs/features/coding-comparison-service.md
// Feature: docs/features/coding-comparison-claim-specific.md
// Feature: docs/features/consolidated-icd10-registry.md
// Feature: docs/features/cms1500-sequencing.md
// Feature: docs/features/optimal-codes-evidence-display.md
// Feature: docs/features/modern-chat-ui-updates.md
// Feature: docs/features/medical-necessity-ncd-integration.md
// Feature: docs/features/force-ocr-payer-response.md
// Feature: docs/features/assign-file-to-claim.md
// Feature: docs/features/display-referenced-policies.md
// Feature: docs/features/json-template-system.md
// Feature: docs/features/nma-strict-json-template.md
// Feature: docs/features/refactor-cpt-codes-to-list.md
// Feature: docs/features/cpt-coding-summary-view.md
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  useColorModeValue,
  Badge,
  Divider,
  Collapse,
  Icon,
  SimpleGrid,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Tag,
  TagLabel,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Link,
  Textarea,
  CircularProgress,
  Select,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon, ExternalLinkIcon, ViewIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { Grid, GridItem, Flex } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import HeaderMenus from './HeaderMenus';
import PayerResponseDisplay from './PayerResponseDisplay';
import AdjustedClaimView from './AdjustedClaimView';
import ModernChat from './ModernChat';
import { callApi } from '../callApi';
import { useContext } from 'react';
import { AppContext } from './AppContext';

const AnalyzeCaseMN = () => {
  const { caseId } = useParams();
  const location = useLocation();
  const { accessToken } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('pretty'); // 'pretty' or 'raw'
  const [claimType, setClaimType] = useState(''); // Default set dynamically
  const [availableClaimTypes, setAvailableClaimTypes] = useState([]);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [hasExistingResults, setHasExistingResults] = useState(false);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [codingSummary, setCodingSummary] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const toast = useToast();

  // State for the new source document viewer modal
  const { isOpen: isSourceOpen, onOpen: onSourceOpen, onClose: onSourceClose } = useDisclosure();
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceContent, setSourceContent] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  // State for Payer Response Tab
  const [claimIds, setClaimIds] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [payerResponseData, setPayerResponseData] = useState(null);
  const [payerResponseLoading, setPayerResponseLoading] = useState(false);
  const [payerResponseError, setPayerResponseError] = useState(null);
  const [forceRegeneration, setForceRegeneration] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  // State for ICD-10 Comparison
  const [icd10ComparisonResults, setIcd10ComparisonResults] = useState(null);
  const [icd10ComparisonLoading, setIcd10ComparisonLoading] = useState(false);
  const [icd10ComparisonError, setIcd10ComparisonError] = useState(null);
  // State for Impact Analysis
  const [impactAnalysisLoading, setImpactAnalysisLoading] = useState(false);
  const [impactAnalysisResult, setImpactAnalysisResult] = useState(null);
  const [impactAnalysisError, setImpactAnalysisError] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isJsonGenerating, setIsJsonGenerating] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonContent, setJsonContent] = useState(null);
  const [pipelineLog, setPipelineLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('nma_cpt_and_med_nec_production_v1');

  // Helper to parse evidence quotes (ported from IOMnew.js)
  const parseEvidenceQuote = (quote) => {
      if (!quote) return { text: '', source: null };
      
      const trimmedQuote = quote.trim();
      if (!trimmedQuote.endsWith(')')) {
          return { text: quote, source: null };
      }

      // Scan backwards to find the balancing opening parenthesis
      let balance = 0;
      let openParenIndex = -1;

      for (let i = trimmedQuote.length - 1; i >= 0; i--) {
          const char = trimmedQuote[i];
          if (char === ')') {
              balance++;
          } else if (char === '(') {
              balance--;
              if (balance === 0) {
                  openParenIndex = i;
                  break;
              }
          }
      }

      if (openParenIndex > 0) {
          return {
              text: trimmedQuote.substring(0, openParenIndex).trim(),
              source: trimmedQuote.substring(openParenIndex + 1, trimmedQuote.length - 1).trim()
          };
      }
      
      return { text: quote, source: null };
  };

  // Helper to normalize filenames for comparison
  const normalizeFilename = (name) => {
      if (!name) return '';
      return name.toLowerCase()
          .replace(/documents_\d+_?/gi, '')  // Remove "Documents_123456_" prefix
          .replace(/[_\/]+/g, ' ')           // Replace underscores and slashes with spaces
          .replace(/\s+/g, ' ')              // Collapse multiple spaces
          .replace(/\(\w\)/g, '')            // Optional: remove (A), (B)
          .trim();
  };

  // Helper to find matching file
  const findMatchingFile = (sourceName, files) => {
      if (!sourceName || !files) return null;
      
      // 1. Try exact match
      let match = files.find(f => f.file_name === sourceName);
      if (match) return match;

      // 2. Try case-insensitive match
      match = files.find(f => f.file_name.toLowerCase() === sourceName.toLowerCase());
      if (match) return match;

      // 3. Try normalized match
      const normSource = normalizeFilename(sourceName);
      
      // Find all potential matches
      const potentialMatches = files.filter(f => {
          const normFile = normalizeFilename(f.file_name);
          return normSource === normFile || 
                 normSource.includes(normFile) || 
                 normFile.includes(normSource);
      });

      if (potentialMatches.length > 0) {
          potentialMatches.sort((a, b) => b.file_name.length - a.file_name.length);
          return potentialMatches[0];
      }
      
      return null;
  };


  // ALL HOOKS MUST BE AT TOP LEVEL
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBlockBg = useColorModeValue('gray.50', 'gray.800');
  const instructionsBg = useColorModeValue('blue.50', 'blue.900');
  const keyBg = useColorModeValue('gray.100', 'gray.600');
  const successBg = useColorModeValue('green.50', 'green.900');
  const claimTypeLabels = {
    pro: 'Professional (26 modifier)',
    tech: 'Technical (TC modifier)',
  };
  const warningBg = useColorModeValue('yellow.50', 'yellow.900');
  const errorBg = useColorModeValue('red.50', 'red.900');
  const actionBg = useColorModeValue('green.50', 'green.900');

  // Centralized helper function for score-based attributes
  const getScoreAttributes = (score) => {
    if (score >= 85) {
      return {
        level: 'High',
        risk: 'Low Risk',
        status: 'Ready to Submit',
        colorScheme: 'green',
        bg: successBg,
        description: 'This case meets medical necessity requirements and is ready for submission.'
      };
    } else if (score >= 50) {
      return {
        level: 'Medium',
        risk: 'Moderate Risk',
        status: 'Review Recommended',
        colorScheme: 'yellow',
        bg: warningBg,
        description: 'This case may benefit from additional documentation or coding review.'
      };
    } else {
      return {
        level: 'Low',
        risk: 'High Risk',
        status: 'Action Needed',
        colorScheme: 'red',
        bg: errorBg,
        description: 'This case requires additional work before submission to avoid denial.'
      };
    }
  };

  // Helper for the 1-100 approval score
  const getApprovalScoreAttributes = (score) => {
    if (score >= 80) {
      return { colorScheme: 'green', label: 'High Approval', status: 'Ready to Submit', description: 'Optimized codes are well-supported.' };
    } else if (score >= 50) {
      return { colorScheme: 'yellow', label: 'Medium Approval', status: 'Review Recommended', description: 'Optimized codes are an improvement.' };
    } else {
      return { colorScheme: 'red', label: 'Low Approval', status: 'Action Needed', description: 'Further research is recommended.' };
    }
  };

  // Component for displaying the 1-100 approval score
  const ApprovalScoreDisplay = ({ score, bestCaseScore }) => {
    const attributes = getApprovalScoreAttributes(score);
    // Use the score directly for the background color logic from the 0-100 scale
    const scoreAttributes = getScoreAttributes(score); 

    return (
      <Card bg={scoreAttributes.bg} borderColor={borderColor} borderWidth="2px" h="100%">
        <CardBody p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
           <VStack spacing={2} textAlign="center">
             <Badge colorScheme={attributes.colorScheme} fontSize="xl" px={4} py={2} borderRadius="md">
               {attributes.label}
             </Badge>
             <Text fontSize="4xl" fontWeight="bold">{score}</Text>
             {bestCaseScore !== undefined && (
                <Text fontSize="sm" color="gray.600" mt="-2 !important">
                  Best Case: {bestCaseScore}/100
                </Text>
             )}
             <Text fontSize="2xl" fontWeight="semibold">
               {attributes.status}
             </Text>
             <Text color="gray.700">
               {attributes.description}
             </Text>
           </VStack>
        </CardBody>
      </Card>
    );
  };

  // Component for the fallback score card when no optimal codes are found
  const FallbackScoreDisplay = ({ score }) => {
    return (
      <Card bg={errorBg} borderColor={borderColor} borderWidth="2px" h="100%">
        <CardBody p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
           <VStack spacing={2} textAlign="center">
             <Badge colorScheme="red" fontSize="xl" px={4} py={2} borderRadius="md">
               Action Needed
             </Badge>
             <Text fontSize="4xl" fontWeight="bold">{score}</Text>
             <Text fontSize="2xl" fontWeight="semibold">
               No Optimal Codes
             </Text>
             <Text color="gray.700">
               Review analysis and documentation to find supported codes.
             </Text>
           </VStack>
        </CardBody>
      </Card>
    );
  };


  // Fetch available claim types on component mount
  useEffect(() => {
    const fetchClaimTypes = async () => {
      if (!caseId || !accessToken) return;

      const queryParams = new URLSearchParams(location.search);
      const urlClaimType = queryParams.get('claim_type');

      try {
        const response = await callApi(`/cases/${caseId}/claim-types`, accessToken);
        const data = await response.json();

        if (response.ok && data.status === 'success' && data.claim_types.length > 0) {
          const fetchedTypes = data.claim_types.filter(type => !type.endsWith('_ncci'));
          setAvailableClaimTypes(fetchedTypes);
          // Set claimType: use from URL if valid, otherwise default to 'pro' if available, else first
          if (urlClaimType && fetchedTypes.includes(urlClaimType)) {
            setClaimType(urlClaimType);
          } else {
            const proType = fetchedTypes.find(t => t.toLowerCase().startsWith('pro'));
            setClaimType(proType || fetchedTypes[0]);
          }
        } else {
          // Fallback to default if API fails or returns no types
          console.warn('Could not fetch dynamic claim types, using defaults.');
          const defaultTypes = ['pro', 'tech'];
          setAvailableClaimTypes(defaultTypes);
          if (urlClaimType && defaultTypes.includes(urlClaimType)) {
            setClaimType(urlClaimType);
          } else {
            setClaimType('pro');
          }
        }
      } catch (err) {
        console.error('Error fetching claim types:', err);
        const defaultTypes = ['pro', 'tech'];
        setAvailableClaimTypes(defaultTypes);
        if (urlClaimType && defaultTypes.includes(urlClaimType)) {
          setClaimType(urlClaimType);
        } else {
          setClaimType('pro');
        }
      }
    };

    fetchClaimTypes();
  }, [caseId, accessToken, location.search]);

  // Fetch available PDF templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!accessToken) return;
      try {
        const response = await callApi('/system_pdf_templates', accessToken);
        if (response.ok) {
          const data = await response.json();
          setAvailableTemplates(data);
          // Ensure default is selected if available, otherwise keep default or select first
          const defaultId = 'standard_cms1500_v1';
          if (data.some(t => t.template_id === defaultId)) {
            setSelectedTemplateId(defaultId);
          } else if (data.length > 0) {
            setSelectedTemplateId(data[0].template_id);
          }
        }
      } catch (err) {
        console.error('Error fetching PDF templates:', err);
      }
    };
    fetchTemplates();
  }, [accessToken]);

  // Check for existing results on component mount or when claimType changes
  useEffect(() => {
    const checkForExistingResults = async () => {
      if (!claimType) return; // Don't run until claimType is set

      try {
        const response = await callApi('/all/analyze_case', accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            external_case_id: caseId,
            claim_type: claimType,
            mode: 'interactive',
            allow_new_analysis: false,
            use_structured_service: true
          }),
        });

        const data = await response.json();
        
        if (response.ok && data.status === 'success') {
          // Results found - display them
          setResults(data);
          setHasExistingResults(true);
          
          // Also fetch the coding summary
          try {
            const summaryResponse = await callApi(`/coding_summary/${caseId}`, accessToken);
            const summaryData = await summaryResponse.json();
            if (summaryResponse.ok) {
              setCodingSummary(summaryData);
            } else {
              console.error("Failed to fetch coding summary on load:", summaryData);
            }
          } catch (summaryErr) {
            console.error("Error fetching coding summary on load:", summaryErr);
          }

          // Try to extract analysis date if available
          if (data.results && data.results.created_at) {
            setAnalysisDate(new Date(data.results.created_at));
          }
        } else if (data.status === 'not_found') {
          // No existing results - this is expected, no error
          setHasExistingResults(false);
        } else {
          // Some other error occurred
          console.error('Error checking for existing results:', data);
        }
      } catch (err) {
        // Network or other error - don't show as main error, just log
        console.error('Error checking for existing results:', err);
        setHasExistingResults(false);
      }
    };

    if (caseId && accessToken) {
      checkForExistingResults();
    }
  }, [caseId, claimType, accessToken]);

  const fetchCaseData = useCallback(async () => {
    if (!caseId || !accessToken) return;
    try {
        // Fetch case details (for files) in parallel with workflow status
        const detailsPromise = callApi(`/get_case_details/${caseId}`, accessToken);
        const workflowPromise = callApi(`/workflow_status/${caseId}`, accessToken);

        const [detailsResponse, workflowResponse] = await Promise.all([detailsPromise, workflowPromise]);

        // Process case details for files
        const detailsData = await detailsResponse.json();
        if (detailsResponse.ok) {
            setCaseFiles(detailsData.files || []);
        } else {
            console.error("Failed to fetch case files:", detailsData.error);
        }

        // Process workflow status
        const workflowData = await workflowResponse.json();
        if (workflowResponse.ok) {
            setWorkflowStatus(workflowData);
        } else {
            // It's okay if this fails, it's supplementary data. Just log it.
            console.warn("Could not fetch workflow status:", workflowData.error);
        }
    } catch (err) {
        console.error("Error fetching initial case data:", err);
    }
  }, [caseId, accessToken]);

  useEffect(() => {
    fetchCaseData();
  }, [fetchCaseData]);

  // Fetch coding summary independently on mount
  useEffect(() => {
    const fetchCodingSummary = async () => {
      if (!caseId || !accessToken) return;
      try {
        const response = await callApi(`/coding_summary/${caseId}`, accessToken);
        if (response.ok) {
          const data = await response.json();
          setCodingSummary(data);
        }
      } catch (err) {
        console.error("Error fetching coding summary:", err);
      }
    };
    fetchCodingSummary();
  }, [caseId, accessToken]);

  // Effect to fetch source content when a source is selected
  useEffect(() => {
    if (!selectedSource || !accessToken) return;

    const fetchSourceContent = async () => {
      setSourceLoading(true);
      setSourceContent('');
      try {
        const sourceId = selectedSource.id;
        if (!sourceId) throw new Error("Source has no valid ID.");

        const response = await callApi(`/content/sources/${sourceId}?disposition=inline`, accessToken);
        
        if (response.ok) {
          const text = await response.text();
          setSourceContent(text);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch source content');
        }
      } catch (err) {
        console.error('Error fetching source content:', err);
        toast({
          title: 'Failed to load source',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setSourceContent(`Error loading content: ${err.message}`);
      } finally {
        setSourceLoading(false);
      }
    };

    fetchSourceContent();
  }, [selectedSource, accessToken, toast]);

  // Fetch available claim IDs for the Payer Response tab
  useEffect(() => {
    const fetchClaimIds = async () => {
      if (!caseId || !accessToken) return;
      try {
        const response = await callApi(`/cases/${caseId}/claims`, accessToken);
        const data = await response.json();
        if (response.ok && data.status === 'success') {
          setClaimIds(data.claim_ids || []);
        } else {
          console.error("Failed to fetch claim IDs:", data.message);
          setClaimIds([]);
        }
      } catch (err) {
        console.error("Error fetching claim IDs:", err);
        setClaimIds([]);
      }
    };
    fetchClaimIds();
  }, [caseId, accessToken]);

  // Function to generate or fetch payer response
  const generatePayerResponse = async (isForced = false) => {
    if (!selectedClaimId) {
      toast({
        title: 'Please select a claim ID.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setPayerResponseLoading(true);
    setPayerResponseError(null);

    try {
      const response = await callApi('/payer_documents/generate-response', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          claim_id: selectedClaimId,
          force_regeneration: isForced,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setPayerResponseData(data);
        toast({
          title: `Response ${data.from_cache ? 'loaded from cache' : 'generated successfully'}.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Failed to generate payer response.');
      }
    } catch (err) {
      console.error('Error generating payer response:', err);
      setPayerResponseError(err.message);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPayerResponseLoading(false);
    }
  };

  const handleCompareCoding = async () => {
    setComparisonLoading(true);
    setComparisonError(null);
    setComparisonResults(null);

    if (!claimType) {
      toast({
        title: 'Claim Type Required',
        description: 'Please select a claim type before running comparison.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      setComparisonLoading(false);
      return;
    }

    try {
      const response = await callApi('/api/v1/compare_coding', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: caseId,
          claim_type: claimType
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setComparisonResults(data.analysis);
        toast({
          title: 'Comparison Complete',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Failed to compare coding.');
      }
    } catch (err) {
      console.error('Error comparing coding:', err);
      setComparisonError(err.message);
      toast({
        title: 'Comparison Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleCompareICD10 = async () => {
    setIcd10ComparisonLoading(true);
    setIcd10ComparisonError(null);
    setIcd10ComparisonResults(null);

    if (!claimType) {
      toast({
        title: 'Claim Type Required',
        description: 'Please select a claim type before running comparison.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      setIcd10ComparisonLoading(false);
      return;
    }

    try {
      const response = await callApi('/api/v1/compare_coding', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: caseId,
          comparison_type: 'icd10',
          claim_type: claimType
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setIcd10ComparisonResults(data.analysis);
        toast({
          title: 'ICD-10 Comparison Complete',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Failed to compare ICD-10 coding.');
      }
    } catch (err) {
      console.error('Error comparing ICD-10 coding:', err);
      setIcd10ComparisonError(err.message);
      toast({
        title: 'Comparison Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIcd10ComparisonLoading(false);
    }
  };

  
  const handleGenerateImpactAnalysis = async () => {
    setImpactAnalysisLoading(true);
    setImpactAnalysisError(null);
    setImpactAnalysisResult(null);

    if (!claimType) {
      toast({
        title: 'Claim Type Required',
        description: 'Please select a claim type before running impact analysis.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      setImpactAnalysisLoading(false);
      return;
    }

    try {
      const response = await callApi('/api/impact-analysis/analyze', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: caseId,
          claim_type: claimType
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setImpactAnalysisResult(data.analysis);
        toast({
          title: 'Impact Analysis Complete',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Failed to generate impact analysis.');
      }
    } catch (err) {
      console.error('Error generating impact analysis:', err);
      setImpactAnalysisError(err.message);
      toast({
        title: 'Analysis Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImpactAnalysisLoading(false);
    }
  };

  const handleAssignClaim = async (fileId, newClaimId) => {
    try {
      const response = await callApi(`/api/v1/cases/${caseId}/files/${fileId}`, accessToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_id: newClaimId || null }),
      });

      if (response.ok) {
        toast({
          title: 'Claim assigned successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchCaseData(); // Refresh the file list
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to assign claim');
      }
    } catch (error) {
      console.error('Error assigning claim:', error);
      toast({
        title: 'Error assigning claim',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUploadForClaimType = async () => {
    if (!claimType) {
      toast({
        title: 'Please select a claim type first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsUploading(true);
      try {
        const formData = new FormData();
        // Hardcode the file type ID for PayerResponse
        formData.append('file_type_id', '1');
        formData.append('claim_id', claimType);
        formData.append('document_category', 'PayerResponse');
        formData.append('force_ocr', 'true'); // Force OCR for Payer Response documents
        formData.append('file', file);

        const response = await callApi(`/upload_file/${caseId}`, accessToken, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          toast({
            title: 'File uploaded successfully!',
            description: `${file.name} has been added to the case.`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          fetchCaseData(); // Refresh the file list
        } else {
          throw new Error(data.error || 'Failed to upload file');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        toast({
          title: 'Upload Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsUploading(false);
      }
    };
    fileInput.click();
  };

  // Automatically fetch response when a claim ID is selected
  useEffect(() => {
    if (selectedClaimId) {
      generatePayerResponse(false); // Initially, always check cache
    } else {
      setPayerResponseData(null); // Clear data if claim ID is deselected
      setPayerResponseError(null);
    }
  }, [selectedClaimId]);

  // Format the date to a more readable format
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  // Helper function to clean and format markdown text for ReactMarkdown
  const cleanMarkdownText = (text) => {
    if (!text) return '';
    return text.replace(/\n\s+/g, '\n\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Handle file click to show content in modal
  const handleFileClick = (file) => {
    setSelectedFile(file);
    onOpen();
  };

  const handleSourceClick = (source) => {
    setSelectedSource(source);
    onSourceOpen();
  };

  const handleFileView = async (file) => {
    setFileLoading(true);
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          file_id: file.id,
          disposition: 'inline' // Tell the backend to serve the file for viewing
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          alert('Please allow pop-ups for this website to view files.');
        }

        // Clean up the object URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);

      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open file');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      toast({
        title: 'Failed to open file',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFileLoading(false);
    }
  };

  const handleFileDownload = async (file) => {
    setFileLoading(true);
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: caseId, // Use external_case_id from URL
          file_id: file.id 
        }),
      });
  
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        toast({
          title: 'File Download Started',
          description: `Downloading ${file.file_name}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFileLoading(false);
    }
  };

  const analyzeMedicalNecessity = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setCodingSummary(null);

    try {
      const response = await callApi('/all/analyze_case', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          external_case_id: caseId,
          claim_type: claimType,
          mode: 'interactive',
          allow_new_analysis: true,
          force_rerun: true, 
          use_pipeline: true,
          use_structured_service: true
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      setResults(data);
      setHasExistingResults(true);

      // After successful analysis, fetch the coding summary
      try {
        const summaryResponse = await callApi(`/coding_summary/${caseId}`, accessToken);
        const summaryData = await summaryResponse.json();
        if (summaryResponse.ok) {
          setCodingSummary(summaryData);
        } else {
          console.error("Failed to fetch coding summary:", summaryData);
        }
      } catch (summaryErr) {
        console.error("Error fetching coding summary:", summaryErr);
        // Don't set main error state for this, just log it
      }
      
      // Update analysis date
      if (data.results && data.results.created_at) {
        setAnalysisDate(new Date(data.results.created_at));
      } else {
        setAnalysisDate(new Date()); // Use current date if not provided
      }
    } catch (err) {
      console.error('Error analyzing medical necessity:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get risk assessment based on score
  const getRiskAssessment = (score) => {
    const attributes = getScoreAttributes(score);
    return {
      level: attributes.risk, // Use risk level for main display
      status: attributes.status,
      colorScheme: attributes.colorScheme,
      bg: attributes.bg,
      description: attributes.description,
    };
  };

  // Component for displaying the main score and risk assessment
  const ScoreDisplay = ({ score }) => {
    const assessment = getRiskAssessment(score);
    
    return (
      <Card bg={assessment.bg} borderColor={borderColor} borderWidth="2px" h="100%">
        <CardBody p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <VStack spacing={3} textAlign="center">
            <Badge colorScheme={assessment.colorScheme} fontSize="xl" px={4} py={2} borderRadius="md">
              {assessment.level}
            </Badge>
            <Text fontSize="4xl" fontWeight="bold">{score}</Text>
            <Text fontSize="2xl" fontWeight="semibold" mt="-2 !important">
              {assessment.status}
            </Text>
            <Text color="gray.700">
              {assessment.description}
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Component for displaying case context
  const CaseContext = ({ payerContext, results }) => {
    return (
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Box>
          <Text fontSize="sm" color="gray.600" fontWeight="semibold">PAYER</Text>
          <Text fontSize="lg">{payerContext?.payer_type || 'Not specified'}</Text>
          {payerContext?.state && <Text fontSize="sm" color="gray.600">{payerContext.state}</Text>}
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.600" fontWeight="semibold">SPECIALTY</Text>
          <Text fontSize="lg">{payerContext?.specialty || 'Not specified'}</Text>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.600" fontWeight="semibold">CASE ID</Text>
          <Text fontSize="lg">{caseId}</Text>
        </Box>
      </SimpleGrid>
    );
  };

  // Component for displaying recommendations
  const RecommendationsDisplay = ({ recommendations }) => {
    if (!recommendations) return null;

    return (
      <Box p={3} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
        <ReactMarkdown>{recommendations}</ReactMarkdown>
      </Box>
    );
  };

  // Component for displaying comprehensive review recommendations
  const ComprehensiveReviewDisplay = ({ diagnosisSuggestions }) => {
    const recommendationText = diagnosisSuggestions?.calls?.[0]?.comprehensive_review_recommendations;

    if (!recommendationText) {
      return null;
    }

    return (
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader p={3}>
          <Heading size="sm">Optimal ICD-10 Search Analysis</Heading>
        </CardHeader>
        <CardBody p={4} pt={0}>
          <Box p={3} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
            <ReactMarkdown>{recommendationText}</ReactMarkdown>
          </Box>
        </CardBody>
      </Card>
    );
  };

  // Standardized confidence badge logic for all components
  const getConfidenceBadge = (score, scale = 100) => {
    // Handle string-based confidence levels like "HIGH", "MEDIUM", "LOW"
    if (typeof score === 'string') {
      const lowerCaseScore = score.toLowerCase();
      if (lowerCaseScore === 'high') {
        return { colorScheme: 'green', label: 'High' };
      }
      if (lowerCaseScore === 'medium') {
        return { colorScheme: 'yellow', label: 'Medium' };
      }
      if (lowerCaseScore === 'low') {
        return { colorScheme: 'red', label: 'Low' };
      }
    }

    // Normalize numeric score to 0-100 if needed
    const normalizedScore = scale === 10 ? score * 10 : score;
    const attributes = getScoreAttributes(normalizedScore);
    return {
      colorScheme: attributes.colorScheme,
      label: attributes.level,
    };
  };

  // Component for displaying diagnosis suggestions
  const DiagnosisSuggestionsDisplay = ({ diagnosisSuggestions }) => {
    if (!diagnosisSuggestions || !diagnosisSuggestions.calls || diagnosisSuggestions.calls.length === 0) {
      return null;
    }

    return (
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <VStack align="start" spacing={2}>
            <Heading size="sm">Diagnosis Suggestions Analysis</Heading>
            <HStack spacing={4}>
              <Badge colorScheme="blue" fontSize="sm">
                Analysis Calls
              </Badge>
              <Badge colorScheme="green" fontSize="sm">
                 Total Suggestions
              </Badge>
            </HStack>
          </VStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {diagnosisSuggestions.calls.map((call, callIndex) => (
              <Box key={callIndex}>
                <VStack align="stretch" spacing={4}>
                  {/* Call Header */}
                  <Box p={3} bg={instructionsBg} borderRadius="md" border="1px solid" borderColor="blue.200">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="semibold" color="blue.700">
                        Analysis #{callIndex + 1}
                      </Text>
                      {call.agent_hint && (
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700">Context:</Text>
                          <Text fontSize="sm" color="gray.600">{call.agent_hint}</Text>
                        </Box>
                      )}
                      <Text fontSize="xs" color="gray.500">
                        Generated: {new Date(call.generated_at).toLocaleString()}
                      </Text>
                    </VStack>
                  </Box>

                  {/* Suggestions */}
                  {call.suggestions && call.suggestions.length > 0 ? (
                    <VStack spacing={3} align="stretch">
                      {call.suggestions.map((suggestion, index) => {
                        const confidenceBadge = getConfidenceBadge(suggestion.confidence, 10);
                        return (
                          <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="white" borderColor={borderColor}>
                            <VStack align="stretch" spacing={3}>
                              {/* Header with ICD-10 and Confidence */}
                              <HStack justify="space-between" align="start">
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="bold" fontSize="lg" color="blue.600">
                                    {suggestion.icd10_code}
                                  </Text>
                                  <Text fontWeight="semibold" color="gray.800">
                                    {suggestion.condition}
                                  </Text>
                                </VStack>
                                <VStack align="end" spacing={1}>
                                  <Badge colorScheme={confidenceBadge.colorScheme} fontSize="sm">
                                    {confidenceBadge.label} Confidence
                                  </Badge>
                                  <Text fontSize="sm" color="gray.500">
                                    {suggestion.confidence}/10
                                  </Text>
                                </VStack>
                              </HStack>

                              {/* Evidence */}
                              {suggestion.evidence && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                                    Evidence from Documentation:
                                  </Text>
                                  <Box p={2} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                                    <Text fontSize="sm" color="gray.700">
                                      {suggestion.evidence}
                                    </Text>
                                  </Box>
                                </Box>
                              )}

                              {/* Chart Review Guidance */}
                              {suggestion.chart_review_guidance && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                                    Chart Review Guidance:
                                  </Text>
                                  <Box p={2} bg={successBg} borderRadius="md" border="1px solid" borderColor="green.200">
                                    <Text fontSize="sm" color="gray.700">
                                      {suggestion.chart_review_guidance}
                                    </Text>
                                  </Box>
                                </Box>
                              )}
                            </VStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  ) : (
                    <Box p={3} bg={warningBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                      <Text color="gray.600" fontStyle="italic" textAlign="center">
                        No specific diagnosis suggestions generated in this analysis call.
                      </Text>
                    </Box>
                  )}
                </VStack>
                
                {/* Divider between calls (except for last one) */}
                {callIndex < diagnosisSuggestions.calls.length - 1 && (
                  <Divider mt={4} />
                )}
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Component for displaying the Clinical Guidance Layer
  const ClinicalGuidanceDisplay = ({ guidance }) => {
    const [showDetails, setShowDetails] = useState(false);

    if (!guidance || !guidance.ai_analysis) {
      return null;
    }

    const {
      needs_review_score,
      analysis_summary,
      excludes1_conflicts,
      excludes2_warnings,
      inclusion_terms
    } = guidance.ai_analysis;

    const getGuidanceStyle = (score) => {
      if (score >= 7) {
        return { status: 'error', title: 'High-Risk Coding Conflicts Found' };
      }
      if (score >= 3) {
        return { status: 'warning', title: 'Potential Coding Issues Found' };
      }
      return { status: 'success', title: 'No Major Coding Conflicts Found' };
    };

    const { status, title } = getGuidanceStyle(needs_review_score);

    const hasDetails = (excludes1_conflicts && excludes1_conflicts.length > 0 && excludes1_conflicts[0] !== '') ||
                       (excludes2_warnings && excludes2_warnings.length > 0 && excludes2_warnings[0] !== '') ||
                       (inclusion_terms && inclusion_terms.length > 0 && inclusion_terms[0] !== '');

    return (
      <Alert
        status={status}
        variant="subtle"
        borderRadius="md"
        mt={4}
        flexDirection="column"
        alignItems="flex-start"
        p={3}
      >
        <HStack>
          <AlertIcon />
          <AlertTitle fontWeight="bold">{title}</AlertTitle>
        </HStack>
        
        {analysis_summary && (
          <Box pt={2} pl={6} width="100%">
            <Box p={2} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <ReactMarkdown>{analysis_summary}</ReactMarkdown>
            </Box>
          </Box>
        )}

        {hasDetails && (
          <Box width="100%" mt={2} pl={6}>
            <Button variant="link" size="sm" onClick={() => setShowDetails(!showDetails)} rightIcon={showDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}>
              {showDetails ? 'Hide Details' : 'Show Rule Details'}
            </Button>
            <Collapse in={showDetails}>
              <VStack align="stretch" spacing={2} mt={2} width="100%">
                {excludes1_conflicts && excludes1_conflicts.length > 0 && excludes1_conflicts[0] !== '' && (
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm">Excludes1 Conflicts:</Text>
                    {excludes1_conflicts.map((item, index) => (
                      <Text key={index} fontSize="xs" pl={2}>- {item}</Text>
                    ))}
                  </Box>
                )}
                {excludes2_warnings && excludes2_warnings.length > 0 && excludes2_warnings[0] !== '' && (
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm">Excludes2 Warnings:</Text>
                    {excludes2_warnings.map((item, index) => (
                      <Text key={index} fontSize="xs" pl={2}>- {item}</Text>
                    ))}
                  </Box>
                )}
                {inclusion_terms && inclusion_terms.length > 0 && inclusion_terms[0] !== '' && (
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm">Relevant Inclusion Terms:</Text>
                    {inclusion_terms.map((item, index) => (
                      <Text key={index} fontSize="xs" pl={2}>- {item}</Text>
                    ))}
                  </Box>
                )}
              </VStack>
            </Collapse>
          </Box>
        )}
      </Alert>
    );
  };

  // Helper to map backend status to readable labels
  const getSourceStatusLabel = (status) => {
    switch (status) {
      case 'primary_contractor_coverage_found':
        return 'State-Specific Policy';
      case 'national_or_other_contractor_guidance_found':
        return 'National Guidance';
      default:
        return 'General';
    }
  };

  // Component for displaying ICD-10 recommendations in a table
  const Icd10RecommendationsTable = ({ recommendations, showCptCoverage = false, showConfidence = true, confidenceMap = {}, evidenceMap = {} }) => {
    if (!recommendations || recommendations.length === 0) return null;

    // Handle legacy string format gracefully to prevent crashes
    if (typeof recommendations === 'string') {
      return (
        <Box p={3} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
          <ReactMarkdown>{recommendations}</ReactMarkdown>
        </Box>
      );
    }

    // Handle new array format
    if (!Array.isArray(recommendations)) {
      return null; // Or some other fallback for unexpected data types
    }

    return (
      <VStack spacing={4} align="stretch">
        {recommendations.map((item, index) => {
          const confidenceValue = confidenceMap[item.code] !== undefined ? confidenceMap[item.code] : item.confidence;
          const confidenceBadge = getConfidenceBadge(confidenceValue);
          
          // Look up detailed evidence from the map
          const detailedEvidence = evidenceMap && evidenceMap[item.code];

          return (
            <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="white" borderColor={borderColor}>
              <VStack align="stretch" spacing={3}>
                {/* Header with ICD-10, Description, Type, and Confidence */}
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" fontSize="lg" color="blue.600">
                      {item.code}
                    </Text>
                    <Text fontWeight="semibold" color="gray.800">
                      {item.description}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={1}>
                    <Badge colorScheme={item.type === 'primary' ? 'blue' : 'gray'} fontSize="sm">
                      {item.type}
                    </Badge>
                    {showConfidence && (
                      <Badge colorScheme={confidenceBadge.colorScheme} fontSize="sm">
                        {confidenceBadge.label} Confidence {typeof confidenceValue === 'number' ? `(${confidenceValue})` : ''}
                      </Badge>
                    )}
                  </VStack>
                </HStack>

                {/* Detailed Evidence from Map */}
                {detailedEvidence && detailedEvidence.evidence && detailedEvidence.evidence.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                      Supporting Evidence:
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {detailedEvidence.evidence.map((quote, idx) => {
                        const { text, source } = parseEvidenceQuote(quote);
                        return (
                          <Box key={idx} p={2} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                            <Text fontSize="sm" color="gray.700" fontStyle="italic">"{text}"</Text>
                            {source && (
                              <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                                — {source}
                              </Text>
                            )}
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {/* Legacy Text Evidence (Fallback or Additional) */}
                {item.text_evidence && (!detailedEvidence || !detailedEvidence.evidence || detailedEvidence.evidence.length === 0) && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                      Evidence Summary:
                    </Text>
                    <Box p={2} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                      <Text fontSize="sm" color="gray.700">
                        {item.text_evidence}
                      </Text>
                    </Box>
                  </Box>
                )}

                {/* Chart Review Guidance */}
                {item.chart_review_guidance && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                      Chart Review Guidance:
                    </Text>
                    <Box p={2} bg={successBg} borderRadius="md" border="1px solid" borderColor="green.200">
                      <Text fontSize="sm" color="gray.700">
                        {item.chart_review_guidance}
                      </Text>
                    </Box>
                  </Box>
                )}

                {/* CPT Coverage Table */}
                {showCptCoverage && item.supporting_cpts && item.supporting_cpts.length > 0 && (
                  <Box pt={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                      CPT Coverage:
                    </Text>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>CPT Code</Th>
                            <Th>Policy Source</Th>
                            <Th>Article</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {item.supporting_cpts.map((cpt, cptIndex) => (
                            <Tr key={cptIndex}>
                              <Td fontFamily="mono">{cpt.cpt_code}</Td>
                              <Td>{getSourceStatusLabel(cpt.source_status)}</Td>
                              <Td>
                                {cpt.source_article ? (
                                  <Link 
                                    href={`https://www.cms.gov/medicare-coverage-database/view/article.aspx?articleId=${cpt.source_article.replace(/\D/g, '')}`} 
                                    isExternal 
                                    color="blue.600"
                                  >
                                    {cpt.source_article} <ExternalLinkIcon mx="2px" />
                                  </Link>
                                ) : (
                                  'N/A'
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    );
  };

  // Component for displaying ICD-10 codes table
  const Icd10CodesTable = ({ icd10Codes }) => {
    if (!icd10Codes) return null;

    // Convert codes object to array and sort alphabetically
    const codesArray = Object.entries(icd10Codes)
      .map(([code, details]) => ({
        code,
        ...details
      }))
      .sort((a, b) => a.code.localeCompare(b.code));

    return (
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>ICD-10 Code</Th>
              <Th>Description</Th>
              <Th>Status</Th>
              <Th>Coverage</Th>
            </Tr>
          </Thead>
          <Tbody>
            {codesArray.map((item) => (
              <Tr key={item.code}>
                <Td fontFamily="mono" fontWeight="semibold">{item.icd10_code || item.code}</Td>
                <Td>{item.description}</Td>
                <Td>
                  <Badge colorScheme={item.status === 'extracted via pattern matching' ? 'blue' : 'gray'} size="sm">
                    {item.status || 'Unknown'}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={item.is_covered ? 'green' : 'red'} size="sm">
                    {item.is_covered ? 'Covered' : 'Not Covered'}
                  </Badge>
                  {item.coverage_note && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {item.coverage_note}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  // Component for displaying CPT codes in card format
  const CptCodesDisplay = ({ cptCodes }) => {
    if (!cptCodes) return null;

    let codesArray = [];

    if (Array.isArray(cptCodes)) {
      // Handle new list format
      codesArray = [...cptCodes].sort((a, b) => {
        const codeA = a.cpt_code || '';
        const codeB = b.cpt_code || '';
        return codeA.localeCompare(codeB);
      });
    } else {
      // Handle legacy object format
      codesArray = Object.entries(cptCodes)
        .map(([key, details]) => ({
          key,
          ...details
        }))
        .sort((a, b) => {
          const codeA = a.cpt_code || a.key;
          const codeB = b.cpt_code || b.key;
          return codeA.localeCompare(codeB);
        });
    }

    const getResultBadge = (result) => {
      if (result === true) return { colorScheme: 'green', label: 'Approved' };
      if (result === false) return { colorScheme: 'red', label: 'Denied' };
      return { colorScheme: 'gray', label: 'Unknown' };
    };

    return (
      <VStack spacing={4} align="stretch">
        {codesArray.map((cptCode, index) => {
          const resultBadge = cptCode.result !== undefined ? getResultBadge(cptCode.result) : null;
          const justification = cptCode.reason || cptCode.justification || '';
          const isPreliminary = justification.toLowerCase().includes('preliminary');
          
          let modifiersList = [];
          if (Array.isArray(cptCode.modifiers)) {
            modifiersList = cptCode.modifiers;
          } else if (typeof cptCode.modifiers === 'string' && cptCode.modifiers.trim() !== '') {
            modifiersList = cptCode.modifiers.split(',').map(m => m.trim());
          } else if (cptCode.modifier) {
            modifiersList = [cptCode.modifier];
          }

          return (
            <Box 
              key={`${cptCode.cpt_code || cptCode.key}-${index}`} 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={isPreliminary ? "red.100" : "white"} 
              borderColor={borderColor}
            >
              <VStack align="stretch" spacing={3}>
                {/* Header with CPT Code and Status */}
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" fontSize="lg" color="blue.600">
                      {cptCode.cpt_code || cptCode.key}
                    </Text>
                    {cptCode.cpt_description && (
                      <Text fontWeight="semibold" color="gray.800" fontSize="sm">
                        {cptCode.cpt_description}
                      </Text>
                    )}
                  </VStack>
                  <VStack align="end" spacing={1}>
                    {resultBadge && (
                      <Badge colorScheme={resultBadge.colorScheme} fontSize="sm">
                        {resultBadge.label}
                      </Badge>
                    )}
                  </VStack>
                </HStack>

                {/* Code Details */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {cptCode.units && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">Units:</Text>
                      <Text fontSize="sm">{cptCode.units}</Text>
                    </Box>
                  )}
                  {modifiersList.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">Modifiers:</Text>
                      <HStack spacing={1} mt={1}>
                        {modifiersList.map((mod, i) => (
                          <Badge key={i} colorScheme="purple" fontSize="xs">{mod}</Badge>
                        ))}
                      </HStack>
                    </Box>
                  )}
                  {cptCode.total_duration && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">Duration:</Text>
                      <Text fontSize="sm">{cptCode.total_duration}</Text>
                    </Box>
                  )}
                </SimpleGrid>

                {/* Child Modalities */}
                {cptCode.child_modalities && cptCode.child_modalities.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                      Modalities:
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {cptCode.child_modalities.map((modality, idx) => (
                        <Badge key={idx} colorScheme="blue" fontSize="xs">
                          {modality}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}

                {/* Reason/Justification */}
                {(cptCode.reason || cptCode.justification) && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                      Clinical Justification:
                    </Text>
                    <Box p={2} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                      <Text fontSize="sm" color="gray.700">
                        {cptCode.reason || cptCode.justification}
                      </Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    );
  };

  // Helper function to determine if a string should be rendered as markdown
  const shouldRenderAsMarkdown = (value) => {
    if (typeof value !== 'string') return false;
    return value.length > 50 || value.includes('\n') || value.includes('**') || value.includes('##');
  };

  // Helper function to format object keys for display
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Recursive component to render JSON with smart formatting
  const JsonRenderer = ({ data, depth = 0 }) => {
    if (data === null || data === undefined) {
      return <Text color="gray.500" fontStyle="italic">null</Text>;
    }

    if (typeof data === 'boolean') {
      return <Badge colorScheme={data ? 'green' : 'red'}>{data.toString()}</Badge>;
    }

    if (typeof data === 'number') {
      return <Text fontWeight="semibold" color="blue.600">{data}</Text>;
    }

    if (typeof data === 'string') {
      if (shouldRenderAsMarkdown(data)) {
        return (
          <Box
            p={3}
            bg={codeBlockBg}
            borderRadius="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <ReactMarkdown>{data}</ReactMarkdown>
          </Box>
        );
      }
      return <Text>{data}</Text>;
    }

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <Text color="gray.500" fontStyle="italic">[]</Text>;
      }
      return (
        <VStack align="stretch" spacing={2} pl={4}>
          {data.map((item, index) => (
            <Box key={index}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                [{index}]
              </Text>
              <JsonRenderer data={item} depth={depth + 1} />
            </Box>
          ))}
        </VStack>
      );
    }

    if (typeof data === 'object') {
      const entries = Object.entries(data);
      if (entries.length === 0) {
        return <Text color="gray.500" fontStyle="italic">{'{}'}</Text>;
      }

      return (
        <VStack align="stretch" spacing={3} pl={depth > 0 ? 4 : 0}>
          {entries.map(([key, value], index) => (
            <Box key={key}>
              <HStack align="flex-start" spacing={3}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  bg={keyBg}
                  px={2}
                  py={1}
                  borderRadius="md"
                  minW="fit-content"
                >
                  {formatKey(key)}
                </Text>
                <Box flex={1}>
                  <JsonRenderer data={value} depth={depth + 1} />
                </Box>
              </HStack>
              {index < entries.length - 1 && <Divider mt={2} />}
            </Box>
          ))}
        </VStack>
      );
    }

    return <Text>{String(data)}</Text>;
  };

  // Component for Diagnosis Evidence Viewer
  const DiagnosisEvidenceViewer = ({ evidenceMap, caseFiles, onFileView, optimalCodes }) => {
    const [selectedCode, setSelectedCode] = useState(null);
    const [selectedDocUrl, setSelectedDocUrl] = useState(null);
    const [isDocLoading, setIsDocLoading] = useState(false);
    const { accessToken } = useContext(AppContext);
    const { caseId } = useParams();
    const toast = useToast();

    // Reset doc viewer when code changes
    useEffect(() => {
        setSelectedDocUrl(null);
    }, [selectedCode]);

    const handleDocView = async (file) => {
        setIsDocLoading(true);
        if (selectedDocUrl) {
            window.URL.revokeObjectURL(selectedDocUrl);
        }
        setSelectedDocUrl(null);

        try {
            const response = await callApi('/download_case_file', accessToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    case_id: caseId,
                    file_id: file.id,
                    disposition: 'inline'
                }),
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setSelectedDocUrl(url);
            } else {
                throw new Error('Failed to load file');
            }
        } catch (error) {
            console.error('Error loading file:', error);
            toast({
                title: 'Failed to load file',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setIsDocLoading(false);
        }
    };

    // Extract optimal code strings for easy lookup
    const optimalCodeSet = useMemo(() => {
        const codes = new Set();
        if (optimalCodes && optimalCodes.recommended_codes) {
            optimalCodes.recommended_codes.forEach(c => codes.add(c.code));
        } else if (optimalCodes && optimalCodes.selected_codes) {
            optimalCodes.selected_codes.forEach(c => codes.add(c.code));
        }
        return codes;
    }, [optimalCodes]);

    if (!evidenceMap || Object.keys(evidenceMap).length === 0) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>No specific diagnosis evidence found.</AlertDescription>
            </Alert>
        );
    }

    const allDiagnoses = Object.entries(evidenceMap).map(([code, data]) => ({
        code,
        ...data
    }));

    const optimalDiagnoses = allDiagnoses.filter(d => optimalCodeSet.has(d.code));
    const otherDiagnoses = allDiagnoses.filter(d => !optimalCodeSet.has(d.code));

    const DiagnosisTable = ({ diagnoses, title, badgeColor }) => (
        <Card bg={cardBg} borderColor={borderColor} mb={4}>
            <CardHeader p={3}>
                <HStack>
                    <Heading size="sm">{title}</Heading>
                    <Badge colorScheme={badgeColor} borderRadius="full" px={2}>
                        {diagnoses.length}
                    </Badge>
                </HStack>
            </CardHeader>
            <CardBody p={0}>
                <TableContainer>
                    <Table variant="simple" size="sm" sx={{ 'tr:hover': { bg: 'blue.50', cursor: 'pointer' } }}>
                        <Thead>
                            <Tr>
                                <Th>Code</Th>
                                <Th>Description</Th>
                                <Th>Evidence Count</Th>
                                <Th>Confidence</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {diagnoses.map((diag) => (
                                <Tr 
                                    key={diag.code} 
                                    onClick={() => setSelectedCode(diag)}
                                    bg={selectedCode?.code === diag.code ? 'blue.100' : 'transparent'}
                                >
                                    <Td fontWeight="bold" color="blue.600" textDecoration="underline">{diag.code}</Td>
                                    <Td>{diag.description || 'No description'}</Td>
                                    <Td>{diag.evidence?.length || 0}</Td>
                                    <Td>
                                        <Badge colorScheme={diag.confidence >= 8 ? 'green' : diag.confidence >= 5 ? 'yellow' : 'red'}>
                                            {diag.confidence}/10
                                        </Badge>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </CardBody>
        </Card>
    );

    return (
        <VStack spacing={6} align="stretch">
            {/* Top: Diagnosis Lists */}
            <Box>
                {optimalDiagnoses.length > 0 && (
                    <DiagnosisTable 
                        diagnoses={optimalDiagnoses} 
                        title="Optimal Codes Evidence" 
                        badgeColor="green" 
                    />
                )}
                
                {otherDiagnoses.length > 0 && (
                    <DiagnosisTable 
                        diagnoses={otherDiagnoses} 
                        title="Other Evidence" 
                        badgeColor="gray" 
                    />
                )}
            </Box>

            {/* Bottom: Split View */}
            {selectedCode && (
                <Grid templateColumns="35% 65%" gap={6} minHeight="600px">
                    {/* Left: Evidence List */}
                    <GridItem overflowY="auto" maxHeight="800px" pr={2}>
                        <Card bg={cardBg} borderColor={borderColor} h="100%">
                            <CardHeader p={3}>
                                <Heading size="sm">Evidence for {selectedCode.code}</Heading>
                            </CardHeader>
                            <CardBody p={4}>
                                <VStack spacing={4} align="stretch">
                                    {selectedCode.evidence && selectedCode.evidence.length > 0 ? (
                                        selectedCode.evidence.map((quote, index) => {
                                            const { text, source } = parseEvidenceQuote(quote);
                                            const matchingFile = findMatchingFile(source, caseFiles);
                                            
                                            return (
                                                <Box key={index} p={3} bg={successBg} borderRadius="md" borderWidth="1px" borderColor="green.200">
                                                    <VStack align="start" spacing={1}>
                                                        <HStack align="start">
                                                            <Icon as={CheckCircleIcon} color="green.500" mt={1} />
                                                            <Text fontSize="sm">"{text}"</Text>
                                                        </HStack>
                                                        {source && (
                                                            <Box pl={6}>
                                                                <Text fontSize="xs" color="gray.500" as="span" mr={1}>Source:</Text>
                                                                {matchingFile ? (
                                                                    <Link 
                                                                        color="blue.600" 
                                                                        fontSize="xs" 
                                                                        fontWeight="medium"
                                                                        onClick={() => handleDocView(matchingFile)}
                                                                    >
                                                                        {source}
                                                                    </Link>
                                                                ) : (
                                                                    <Text fontSize="xs" as="span" fontStyle="italic">{source}</Text>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </VStack>
                                                </Box>
                                            );
                                        })
                                    ) : (
                                        <Text color="gray.500" fontStyle="italic">No specific quotes found.</Text>
                                    )}
                                    {selectedCode.notes && (
                                        <Box mt={4}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.500">NOTES</Text>
                                            <Text fontSize="sm">{selectedCode.notes}</Text>
                                        </Box>
                                    )}
                                </VStack>
                            </CardBody>
                        </Card>
                    </GridItem>

                    {/* Right: Document Viewer */}
                    <GridItem bg="gray.100" borderRadius="md" borderWidth="1px" position="relative" height="800px">
                        {isDocLoading ? (
                            <Flex justify="center" align="center" h="100%">
                                <VStack>
                                    <Spinner size="xl" color="blue.500" />
                                    <Text>Loading Document...</Text>
                                </VStack>
                            </Flex>
                        ) : selectedDocUrl ? (
                            <iframe 
                                src={selectedDocUrl} 
                                title="Document Viewer"
                                width="100%" 
                                height="100%" 
                                style={{ border: 'none', borderRadius: '4px' }} 
                            />
                        ) : (
                            <Flex justify="center" align="center" h="100%" color="gray.500" flexDirection="column">
                                <Text fontSize="lg">Select a source to view</Text>
                                <Text fontSize="sm">Click a source link in the evidence list</Text>
                            </Flex>
                        )}
                    </GridItem>
                </Grid>
            )}
        </VStack>
    );
  };

  // Component for displaying Referenced Policies
  const ReferencedPoliciesDisplay = ({ policies }) => {
    if (!policies || (!policies.used?.length && !policies.available?.length)) {
      return null;
    }

    const PolicyList = ({ items, title, colorScheme }) => (
      <Box mb={4}>
        <Heading size="xs" mb={2} textTransform="uppercase" color="gray.500">
          {title}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
          {items.map((policy) => (
            <Link 
              key={policy.id} 
              href={policy.url} 
              isExternal 
              _hover={{ textDecoration: 'none' }}
            >
              <Box 
                p={3} 
                borderWidth="1px" 
                borderRadius="md" 
                bg="white" 
                borderColor={borderColor}
                _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
                height="100%"
              >
                <HStack justify="space-between" mb={1}>
                  <Badge colorScheme={colorScheme}>{policy.type || 'Policy'}</Badge>
                  <Icon as={ExternalLinkIcon} color="gray.400" boxSize={3} />
                </HStack>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" mb={1}>
                  {policy.id}
                </Text>
                <Text fontSize="xs" color="gray.600" noOfLines={2}>
                  {policy.title}
                </Text>
              </Box>
            </Link>
          ))}
        </SimpleGrid>
      </Box>
    );

    return (
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardHeader p={3}>
          <Heading size="sm">Referenced Coverage Policies</Heading>
          <Text fontSize="sm" color="gray.600">
            Official CMS policies referenced during this analysis.
          </Text>
        </CardHeader>
        <CardBody p={4}>
          {policies.used && policies.used.length > 0 && (
            <PolicyList 
              items={policies.used} 
              title="Used in Analysis" 
              colorScheme="green" 
            />
          )}
          
          {policies.available && policies.available.length > 0 && (
            <>
              {policies.used && policies.used.length > 0 && <Divider my={4} />}
              <PolicyList 
                items={policies.available} 
                title="Available for Review" 
                colorScheme="gray" 
              />
            </>
          )}
        </CardBody>
      </Card>
    );
  };

  // Component for displaying the Consolidated ICD-10 Registry
  const Icd10RegistryDisplay = ({ registry, referencedPolicies }) => {
    if (!registry || registry.length === 0) {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>No consolidated ICD-10 registry data available.</AlertDescription>
        </Alert>
      );
    }

    return (
      <VStack spacing={6} align="stretch">
        {referencedPolicies && (
          <ReferencedPoliciesDisplay policies={referencedPolicies} />
        )}
        
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader p={3}>
            <Heading size="sm">Consolidated ICD-10 Registry</Heading>
            <Text fontSize="sm" color="gray.600">
              Complete lifecycle view of all diagnosis codes found during analysis.
            </Text>
          </CardHeader>
          <CardBody p={0}>
            <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Description</Th>
                  <Th>Sources</Th>
                  <Th>Status</Th>
                  <Th>Evidence</Th>
                  <Th>Coverage</Th>
                </Tr>
              </Thead>
              <Tbody>
                {registry.map((item, index) => (
                  <Tr key={index} bg={item.status_flags.is_in_optimal_list ? 'green.50' : 'transparent'}>
                    <Td fontWeight="bold" fontFamily="mono" color="blue.600">
                      {item.code}
                    </Td>
                    <Td maxW="300px" whiteSpace="normal">
                      {item.description}
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {item.sources.map((source, idx) => (
                          <Badge key={idx} size="sm" colorScheme="gray" variant="outline">
                            {source.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {item.status_flags.is_in_optimal_list && (
                          <Badge colorScheme="green">Optimal</Badge>
                        )}
                        {item.status_flags.is_rejected_by_curation && (
                          <Badge colorScheme="orange">Filtered Out</Badge>
                        )}
                        {item.status_flags.is_in_original_text && (
                          <Badge colorScheme="blue">Original</Badge>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      {item.evidence?.text_extracts?.length > 0 ? (
                        <VStack align="start" spacing={1}>
                          <Badge colorScheme="green" variant="subtle">
                            {item.evidence.text_extracts.length} Quote(s)
                          </Badge>
                          {item.evidence.confidence_score > 0 && (
                            <Text fontSize="xs" color="gray.500">
                              Conf: {item.evidence.confidence_score}/10
                            </Text>
                          )}
                        </VStack>
                      ) : (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic">None</Text>
                      )}
                    </Td>
                    <Td>
                      {item.medical_necessity?.coverage_status === 'covered' ? (
                        <Badge colorScheme="green">Covered</Badge>
                      ) : (
                        <Badge colorScheme="gray">Unknown</Badge>
                      )}
                      {item.medical_necessity?.supports_cpt_codes?.length > 0 && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          For: {item.medical_necessity.supports_cpt_codes.join(', ')}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </VStack>
    );
  };

  // Component for displaying CMS-1500 Sequencing Data
  const CMS1500SequencingDisplay = ({ cmsData }) => {
    if (!cmsData || !cmsData.box_21 || cmsData.box_21.length === 0) {
      return null;
    }

    return (
      <Card bg={cardBg} borderColor={borderColor} mt={6}>
        <CardHeader p={3}>
          <Heading size="sm">CMS-1500 Diagnosis Sequencing (Box 21 & 24E)</Heading>
          <Text fontSize="sm" color="gray.600">
            Optimized diagnosis ordering and CPT linkage for claim submission.
          </Text>
        </CardHeader>
        <CardBody p={4} pt={0}>
          <VStack spacing={6} align="stretch">
            {/* Box 21: Diagnosis List */}
            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.700">Box 21: Diagnosis Codes (A-L)</Text>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th width="50px">Tag</Th>
                      <Th width="100px">Code</Th>
                      <Th>Description</Th>
                      <Th>Reason for Sequencing</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cmsData.box_21.map((diag) => (
                      <Tr key={diag.tag}>
                        <Td fontWeight="bold" fontSize="lg" color="blue.600">{diag.tag}</Td>
                        <Td fontFamily="mono" fontWeight="semibold">{diag.code}</Td>
                        <Td>{diag.description}</Td>
                        <Td fontSize="sm" color="gray.600">{diag.reason}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Divider />

            {/* Box 24E: Diagnosis Pointers */}
            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.700">Box 24E: Diagnosis Pointers per CPT</Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {Object.entries(cmsData.box_24e || {}).map(([cpt, pointers]) => (
                  <Box key={cpt} p={3} borderWidth="1px" borderRadius="md" bg="white">
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="bold" fontFamily="mono">{cpt}</Text>
                      <Badge colorScheme="blue">{pointers.length} Link(s)</Badge>
                    </HStack>
                    <HStack spacing={2}>
                      {pointers.map((pointer) => (
                        <Tag key={pointer} size="md" variant="solid" colorScheme="blue">
                          <TagLabel fontWeight="bold">{pointer}</TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Links to: {pointers.map(p => {
                        const diag = cmsData.box_21.find(d => d.tag === p);
                        return diag ? diag.code : '?';
                      }).join(', ')}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Component for the ICD-10 Sandbox Tab
  const Icd10SandboxTabContent = ({ results, caseId, accessToken }) => {
    const [sandboxCodes, setSandboxCodes] = useState('');
    const [sandboxResult, setSandboxResult] = useState(null);
    const [isSandboxLoading, setIsSandboxLoading] = useState(false);
    const [sandboxError, setSandboxError] = useState(null);
    const toast = useToast();

    const handleLoadOptimal = () => {
      const codes = results?.results?.optimal_icd10_codes?.recommended_codes?.map(c => c.code) || [];
      if (codes.length === 0) {
        toast({ title: 'No optimal codes available to load.', status: 'info', duration: 3000 });
      }
      setSandboxCodes(codes.join('\n'));
    };

    const handleLoadBestCase = () => {
      const codes = results?.results?.icd10_best_case?.map(c => c.code) || [];
      if (codes.length === 0) {
        toast({ title: 'No best-case codes available to load.', status: 'info', duration: 3000 });
      }
      setSandboxCodes(codes.join('\n'));
    };

    const handleAnalyze = async () => {
      setIsSandboxLoading(true);
      setSandboxResult(null);
      setSandboxError(null);

      const codesArray = sandboxCodes
        .split(/[\s,]+/)
        .map(code => code.trim().toUpperCase())
        .filter(code => code);

      if (codesArray.length === 0) {
        toast({ title: 'Please enter at least one ICD-10 code to analyze.', status: 'warning', duration: 3000, isClosable: true });
        setIsSandboxLoading(false);
        return;
      }

      try {
        const response = await callApi(`/coding-tools/cases/${caseId}/analyze-icd10-conflicts`, accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icd10_codes: codesArray }),
        });
        const data = await response.json();

        if (!response.ok) {
          if (response.status === 400 && data.invalid_codes) {
            const errorMessage = `The following codes are invalid: ${data.invalid_codes.join(', ')}. Please correct them and try again.`;
            setSandboxError(errorMessage);
            toast({ title: 'Validation Error', description: errorMessage, status: 'error', duration: 7000, isClosable: true });
          } else {
            throw new Error(data.message || data.error || 'Failed to analyze codes.');
          }
        } else {
          setSandboxResult(data.guidance);
        }
      } catch (err) {
        const errorMessage = err.message || 'An unexpected error occurred.';
        setSandboxError(errorMessage);
        toast({ title: 'Analysis Error', description: errorMessage, status: 'error', duration: 5000, isClosable: true });
      } finally {
        setIsSandboxLoading(false);
      }
    };

    return (
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">ICD-10 Sandbox</Heading>
          <Text fontSize="sm" color="gray.600">
            Interactively test ICD-10 code combinations for conflicts against this case's documentation.
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Load Codes from Analysis</FormLabel>
              <HStack>
                <Button size="sm" onClick={handleLoadOptimal} isDisabled={!results}>
                  Load Optimal Codes
                </Button>
                <Button size="sm" onClick={handleLoadBestCase} isDisabled={!results}>
                  Load Best-Case Codes
                </Button>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">ICD-10 Codes</FormLabel>
              <Textarea
                placeholder="Enter ICD-10 codes, separated by commas or new lines..."
                value={sandboxCodes}
                onChange={(e) => setSandboxCodes(e.target.value)}
                rows={8}
                fontFamily="monospace"
              />
            </FormControl>
            <Button
              colorScheme="blue"
              onClick={handleAnalyze}
              isLoading={isSandboxLoading}
              loadingText="Analyzing..."
            >
              Analyze Conflicts
            </Button>

            {isSandboxLoading && (
              <VStack justifyContent="center" p={4}>
                <CircularProgress isIndeterminate color="blue.300" />
                <Text>Analyzing codes...</Text>
              </VStack>
            )}

            {sandboxError && !isSandboxLoading && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{sandboxError}</AlertDescription>
              </Alert>
            )}

            {sandboxResult && !isSandboxLoading && (
              <Box mt={4}>
                <Heading size="sm" mb={2}>Analysis Results</Heading>
                <ClinicalGuidanceDisplay guidance={sandboxResult} />
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Component for the Payer Response Tab
  const PayerResponseTabContent = () => {
    return (
      <VStack spacing={4} align="stretch">
        {/* Link to V2 Dashboard */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="2px" borderColor="purple.300">
          <CardBody p={4}>
            <HStack justify="space-between" align="center" wrap="wrap" spacing={4}>
              <VStack align="start" spacing={1}>
                <Heading size="sm" color="purple.700">Payer Response Intelligence Dashboard</Heading>
                <Text fontSize="sm" color="gray.600">
                  Open the dedicated V2 dashboard to view the 4-stage pipeline results, validation report, and draft appeal letter.
                </Text>
              </VStack>
              <Button
                as={RouterLink}
                to={`/payer-response-intelligence/${caseId}`}
                colorScheme="purple"
                rightIcon={<ExternalLinkIcon />}
              >
                Open Dashboard
              </Button>
            </HStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader p={3}>
            <Heading size="sm">Generate Payer Response Letter</Heading>
          </CardHeader>
          <CardBody p={4} pt={0}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Select Claim ID</FormLabel>
                {claimIds.length > 0 ? (
                  <Select
                    placeholder="Select a claim to generate a response for"
                    value={selectedClaimId}
                    onChange={(e) => setSelectedClaimId(e.target.value)}
                  >
                    {claimIds.map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No claim IDs found for this case. Ensure documents are uploaded with a claim ID.
                  </Text>
                )}
              </FormControl>
              {selectedClaimId && (
                <HStack>
                  <Button
                    colorScheme="blue"
                    onClick={() => generatePayerResponse(forceRegeneration)}
                    isLoading={payerResponseLoading}
                    loadingText="Generating..."
                    isDisabled={!selectedClaimId || payerResponseLoading}
                  >
                    {payerResponseData ? 'Re-generate Response' : 'Generate Response'}
                  </Button>
                  <Checkbox
                    isChecked={forceRegeneration}
                    onChange={(e) => setForceRegeneration(e.target.checked)}
                  >
                    Force re-generation from scratch
                  </Checkbox>
                </HStack>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Upload Section */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader p={3}>
            <Heading size="sm">Upload Payer Response Document</Heading>
          </CardHeader>
          <CardBody p={4} pt={0}>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Upload a document (e.g., denial letter, EOB) to be associated with the currently selected claim type.
              </Text>
              <Text>
                Claim Type for Upload: <Badge colorScheme="teal">{claimTypeLabels[claimType] || claimType}</Badge>
              </Text>
              <Button
                colorScheme="teal"
                onClick={handleUploadForClaimType}
                isLoading={isUploading}
                loadingText="Uploading..."
                isDisabled={!claimType || isUploading}
              >
                Upload Document
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {payerResponseLoading && (
          <Card bg={cardBg} borderColor={borderColor} p={6} textAlign="center">
            <VStack>
              <CircularProgress isIndeterminate color="blue.300" />
              <Text>Generating response letter...</Text>
              <Text fontSize="sm" color="gray.500">This may take up to 30 seconds.</Text>
            </VStack>
          </Card>
        )}

        {payerResponseError && !payerResponseLoading && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle>Generation Error</AlertTitle>
            <AlertDescription>{payerResponseError}</AlertDescription>
          </Alert>
        )}

        {payerResponseData && !payerResponseLoading && (
          <PayerResponseDisplay response={payerResponseData} />
        )}
      </VStack>
    );
  };

  // Component for the Case Documents Tab
  const CaseDocumentsTabContent = ({ files, handleFileClick, formatFileSize, formatDate, handleFileView, handleFileDownload, fileLoading, availableClaimTypes, handleAssignClaim }) => {
    const groupedFiles = [...files]
      .sort((a, b) => a.file_name.localeCompare(b.file_name))
      .reduce((acc, file) => {
        const category = file.document_category;
        if (category === 'RevedyReportInternal') {
          acc.medicalNecessity.push(file);
        } else if (category === 'PayerResponse') {
          acc.payerResponse.push(file);
        } else if (category === 'PayerResponseInternal') {
          acc.payerResponseAnalysis.push(file);
        } else {
          acc.caseDocuments.push(file);
        }
        return acc;
      }, {
        medicalNecessity: [],
        payerResponse: [],
        payerResponseAnalysis: [],
        caseDocuments: []
      });

    const FileTable = ({ files }) => (
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>File Name</Th>
              <Th>Document Category</Th>
              <Th>Claim</Th>
              <Th>Size</Th>
              <Th>Uploaded</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {files.map(file => (
              <Tr key={file.id}>
                <Td
                  color="blue.600"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={() => handleFileClick(file)}
                >
                  {file.file_name}
                </Td>
                <Td>{file.document_category}</Td>
                <Td>
                  <Select
                    size="xs"
                    value={file.claim_id || ''}
                    onChange={(e) => handleAssignClaim(file.id, e.target.value)}
                    placeholder="Unassigned"
                    width="120px"
                  >
                    {availableClaimTypes.map(type => (
                      <option key={type} value={type}>{claimTypeLabels[type] || type}</option>
                    ))}
                  </Select>
                </Td>
                <Td>{formatFileSize(file.file_size)}</Td>
                <Td>{formatDate(file.upload_date)}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<ViewIcon />}
                      size="sm"
                      aria-label="View file"
                      title="View file in new tab"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileView(file);
                      }}
                      isLoading={fileLoading}
                    />
                    <IconButton
                      icon={<DownloadIcon />}
                      size="sm"
                      aria-label="Download file"
                      title="Download file"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileDownload(file);
                      }}
                      isLoading={fileLoading}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );

    if (files.length === 0) {
      return (
        <Text color="gray.500" fontStyle="italic">
          No documents found for this case.
        </Text>
      );
    }

    return (
      <VStack spacing={6} align="stretch">
        {groupedFiles.medicalNecessity.length > 0 && (
          <Box>
            <Heading size="sm" mb={2}>Medical Necessity</Heading>
            <FileTable files={groupedFiles.medicalNecessity} />
          </Box>
        )}
        {groupedFiles.payerResponse.length > 0 && (
          <Box>
            <Heading size="sm" mb={2}>Payer Response</Heading>
            <FileTable files={groupedFiles.payerResponse} />
          </Box>
        )}
        {groupedFiles.payerResponseAnalysis.length > 0 && (
          <Box>
            <Heading size="sm" mb={2}>Payer Response Analysis</Heading>
            <FileTable files={groupedFiles.payerResponseAnalysis} />
          </Box>
        )}
        {groupedFiles.caseDocuments.length > 0 && (
          <Box>
            <Heading size="sm" mb={2}>Case Documents</Heading>
            <FileTable files={groupedFiles.caseDocuments} />
          </Box>
        )}
      </VStack>
    );
  };

  // Parse supporting evidence to extract used documents
  const usedDocuments = useMemo(() => {
    const supportingEvidence = results?.results?.supporting_evidence;
    if (!supportingEvidence) return [];

    const docs = [];
    const lines = supportingEvidence.split('\n');
    
    lines.forEach(line => {
      // Look for content_id patterns
      const contentMatch = line.match(/content_id:\s*(\d+),\s*title:\s*([^:]+):/);
      if (contentMatch) {
        docs.push({
          id: contentMatch[1],
          title: contentMatch[2].trim(),
          type: 'Internal Policy'
        });
      }
      
      // Look for web search results
      const webMatch = line.match(/Web Search Results,\s*title:\s*([^:]+):/);
      if (webMatch) {
        docs.push({
          title: webMatch[1].trim(),
          type: 'Payer Website'
        });
      }
    });
    
    return docs;
  }, [results]);

  // Create a confidence map from the best_case recommendations for consistent display
  const confidenceMap = useMemo(() => {
    const map = {};
    const bestCase = results?.results?.icd10_best_case;
    if (Array.isArray(bestCase)) {
      bestCase.forEach(item => {
        map[item.code] = item.confidence;
      });
    }
    return map;
  }, [results]);

  const handleGeneratePdf = async (templateId = null) => {
    const idToUse = templateId || selectedTemplateId;
    if (!caseId || !claimType) {
      toast({
        title: 'Cannot generate PDF',
        description: 'Case ID or Claim Type is missing.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsPdfGenerating(true);
    try {
      const response = await callApi(`/v2/cases/${caseId}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: idToUse,
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          // Fallback for browsers that block popups
          toast({
            title: 'Please allow pop-ups',
            description: 'Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.',
            status: 'warning',
            duration: 7000,
            isClosable: true,
          });
        }
        // Clean up the object URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast({
        title: 'PDF Generation Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleGenerateJson = async (templateId = null) => {
    // For JSON, we don't use the PDF template selector. 
    // Default to the legacy NMA Pro JSON template to ensure consistent comparison.
    const idToUse = templateId || 'nma_pro_json'; 
    if (!caseId || !claimType) {
      toast({
        title: 'Cannot generate JSON',
        description: 'Case ID or Claim Type is missing.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsJsonGenerating(true);
    try {
      const response = await callApi(`/integration/cases/${caseId}/generate-json-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: idToUse,
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setJsonContent(data);
        setJsonModalOpen(true);
        
        toast({
          title: 'JSON Report Generated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate JSON report.');
      }
    } catch (err) {
      console.error('Error generating JSON report:', err);
      toast({
        title: 'JSON Generation Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsJsonGenerating(false);
    }
  };

  const fetchPipelineLog = async () => {
    setLogLoading(true);
    setLogError(null);
    setPipelineLog(null);
    try {
      const response = await callApi(`/admin/pipeline-log/${caseId}`, accessToken);
      const data = await response.json();
      if (response.ok && data.status === 'success') {
        if (data.data.length === 0) {
          toast({
            title: 'No Logs Found',
            description: 'No pipeline logs were found for this case.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
        setPipelineLog(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch pipeline log.');
      }
    } catch (err) {
      console.error('Error fetching pipeline log:', err);
      setLogError(err.message);
      toast({
        title: 'Error Loading Log',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLogLoading(false);
    }
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={4}>
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <Box>
            <Heading as="h1" size="xl" mb={2}>
              Medical Necessity Analysis
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Analyze medical necessity for existing adjusted CPT codes using AI-powered agents
            </Text>
          </Box>

          {/* Case Info and Controls - Combined Layout */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Card bg={cardBg} borderColor={borderColor}>
              <CardHeader p={3}>
                <Heading size="sm">Case Information</Heading>
              </CardHeader>
              <CardBody p={4} pt={0}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="md">
                    <strong>Case ID:</strong> {caseId}
                  </Text>
                  {workflowStatus?.step2_carrier_assignment?.completed && (
                    <Text fontSize="md">
                      <strong>Payer:</strong> {workflowStatus.step2_carrier_assignment.carrier_name} ({workflowStatus.step2_carrier_assignment.payer_type})
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            <Card bg={cardBg} borderColor={borderColor}>
              <CardHeader p={3}>
                <Heading size="sm">Analysis Settings</Heading>
              </CardHeader>
              <CardBody p={4} pt={0}>
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm">Claim Type</FormLabel>
                    <RadioGroup value={claimType} onChange={setClaimType}>
                      <VStack spacing={2} align="start">
                        {availableClaimTypes.length > 0 ? (
                          availableClaimTypes.map((type) => (
                            <Radio key={type} value={type} size="sm">
                              {claimTypeLabels[type] || type}
                            </Radio>
                          ))
                        ) : (
                          <Text fontSize="sm" color="gray.500">Loading claim types...</Text>
                        )}
                      </VStack>
                    </RadioGroup>
                  </FormControl>

                  <Button
                    colorScheme="blue"
                    size="md"
                    onClick={analyzeMedicalNecessity}
                    isLoading={loading}
                    loadingText="Analyzing..."
                    isDisabled={loading}
                    width="100%"
                  >
                    {hasExistingResults ? 'Re-run Analysis' : 'Analyze Medical Necessity'}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Loading State */}
          {loading && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody p={4}>
                <VStack spacing={3}>
                  <Spinner size="lg" color="blue.500" />
                  <Text fontSize="md">Analyzing medical necessity...</Text>
                  <Text fontSize="sm" color="gray.500">
                    Using AI agents to analyze CPT-to-ICD-10 validation and payer policies.
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    This analysis typically takes 10-20 seconds.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Error Display */}
          {error && !loading && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Analysis Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Results Display */}
          {results && !loading && !error && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardHeader>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <Heading size="md">Medical Necessity Analysis Results</Heading>
                    {analysisDate && (
                      <Text fontSize="sm" color="gray.500">
                        Analysis run: {analysisDate.toLocaleDateString()} at {analysisDate.toLocaleTimeString()}
                      </Text>
                    )}
                  </VStack>
                  <HStack spacing={3}>
                    {results.status && (
                      <Badge
                        colorScheme={results.status === 'success' ? 'green' : 'red'}
                        fontSize="sm"
                        px={3}
                        py={1}
                      >
                        {results.status.toUpperCase()}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setViewMode(viewMode === 'pretty' ? 'raw' : 'pretty')}
                    >
                      {viewMode === 'pretty' ? 'Show Raw JSON' : 'Show Pretty View'}
                    </Button>

                    <HStack spacing={2}>
                      <Select 
                        size="sm" 
                        width="250px" 
                        value={selectedTemplateId} 
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                      >
                        {availableTemplates.map(t => (
                          <option key={t.template_id} value={t.template_id}>
                            {t.name}
                          </option>
                        ))}
                        {availableTemplates.length === 0 && (
                          <option value="standard_cms1500_v1">Standard CMS-1500 Report</option>
                        )}
                      </Select>
                      <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<DownloadIcon />}
                        onClick={() => handleGeneratePdf()}
                        isLoading={isPdfGenerating}
                        loadingText="Generating..."
                      >
                        Generate PDF
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<DownloadIcon />}
                        onClick={() => handleGenerateJson()}
                        isLoading={isJsonGenerating}
                        loadingText="Generating..."
                      >
                        Generate JSON
                      </Button>
                    </HStack>
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody p={4}>
                {viewMode === 'pretty' ? (
                  <VStack spacing={4} align="stretch">
                    {/* Case Description */}
                    {results.case_description && (
                      <Card bg={cardBg} borderColor={borderColor}>
                        <CardBody>
                          <Text fontSize="lg" fontWeight="semibold">
                            {results.case_description}
                          </Text>
                        </CardBody>
                      </Card>
                    )}

                    {/* Score Display */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {results.results?.approval_assessment_score !== undefined && (
                        <Box>
                          <Heading size="sm" mb={2} textAlign="center">Current Submission Score</Heading>
                          <ScoreDisplay score={results.results.approval_assessment_score} />
                        </Box>
                      )}
                      {results.results?.optimal_icd10_codes?.approval_score !== undefined ? (
                        <Box>
                          <Heading size="sm" mb={2} textAlign="center">Optimal Approval Score</Heading>
                          <ApprovalScoreDisplay 
                            score={results.results.optimal_icd10_codes.approval_score} 
                            bestCaseScore={results.results.best_case_assessment_score}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <Heading size="sm" mb={2} textAlign="center">Optimal Approval Score</Heading>
                          <FallbackScoreDisplay score={results.results.approval_assessment_score} />
                        </Box>
                      )}
                    </SimpleGrid>

                    {/* Fallback Alert */}
                    {(() => {
                      const searchLog = results.results?.search_log;
                      if (!searchLog) return null;

                      const coveredCodesFallback = searchLog.find(
                        (log) => log.search_type === 'covered_codes' && log.search_level === 'national_fallback'
                      );

                      if (coveredCodesFallback) {
                        const contractors = coveredCodesFallback.contractors_searched;
                        const contractorName = contractors && contractors.length > 0
                          ? [...new Set(contractors.map(c => c.name))].join(', ')
                          : 'Unknown Contractor';

                        return (
                          <Alert status="warning" borderRadius="md" mt={4}>
                            <AlertIcon />
                            <Box>
                              <AlertDescription>
                                Contractor: {contractorName} - No state-specific coverage for CPT codes found, using national fallback data.
                              </AlertDescription>
                            </Box>
                          </Alert>
                        );
                      }
                      return null;
                    })()}

                    {/* Case Context */}
                    {results.results?.payer_context && (
                      <Card bg={cardBg} borderColor={borderColor}>
                        <CardHeader p={3}>
                          <Heading size="sm">Case Context</Heading>
                        </CardHeader>
                        <CardBody p={4} pt={0}>
                          <CaseContext 
                            payerContext={results.results.payer_context} 
                            results={results.results}
                          />
                        </CardBody>
                      </Card>
                    )}

                    {/* Tabbed Content */}
                    <Tabs variant="enclosed" colorScheme="blue">
                      <TabList>
                        <Tab>Medical Necessity</Tab>
                        <Tab>ICD-10 Registry</Tab>
                        <Tab>Diagnosis Evidence</Tab>
                        <Tab>CPT Coding</Tab>
                        <Tab>CMS 1500</Tab>
                        {codingSummary?.adjusted_codes?.[`${claimType}_ncci`] && (
                          <Tab>CMS 1500 (NCCI Validated)</Tab>
                        )}
                        <Tab>Payer Response</Tab>
                        <Tab>Case Documents <Badge ml={2}>{caseFiles.length}</Badge></Tab>
                        <Tab>ICD-10 Sandbox</Tab>
                        <Tab>Chat Assistant</Tab>
                        <Tab>Pipeline Log</Tab>
                      </TabList>
                      <TabPanels>
                        {/* Tab 1: Medical Necessity */}
                        <TabPanel px={0} py={2}>
                          <VStack spacing={4} align="stretch">

                            {/* Recommendations */}
                            {results.results?.recommendations && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Recommendations</Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <RecommendationsDisplay recommendations={results.results.recommendations} />
                                </CardBody>
                              </Card>
                            )}

                            {/* Comprehensive Review Recommendations */}
                            <ComprehensiveReviewDisplay diagnosisSuggestions={results.results?.diagnosis_suggestions} />

                            {/* Optimal ICD-10 Recommendations */}
                            {results.results?.optimal_icd10_codes && results.results.optimal_icd10_codes.recommended_codes && results.results.optimal_icd10_codes.recommended_codes.length > 0 ? (
                              <Card bg={cardBg} borderColor={borderColor} borderWidth="2px" borderColor="green.300">
                                <CardHeader p={3}>
                                  <VStack align="start" spacing={2}>
                                    <Heading size="sm">Optimal ICD-10 Recommendations</Heading>
                                    <HStack spacing={4}>
                                      {results.results.optimal_icd10_codes.confidence_level && (
                                        <Badge colorScheme="green">
                                          Confidence: {results.results.optimal_icd10_codes.confidence_level}
                                        </Badge>
                                      )}
                                      <Badge colorScheme="blue">
                                        {results.results.optimal_icd10_codes.total_selected} Selected / {results.results.optimal_icd10_codes.total_available} Available
                                      </Badge>
                                    </HStack>
                                  </VStack>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <VStack spacing={4} align="stretch">
                                    {results.results.optimal_icd10_codes.selection_summary && (
                                      <Box p={3} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                                        <ReactMarkdown>{results.results.optimal_icd10_codes.selection_summary}</ReactMarkdown>
                                      </Box>
                                    )}
                                    <Icd10RecommendationsTable 
                                      recommendations={results.results.optimal_icd10_codes.recommended_codes} 
                                      showCptCoverage={true} 
                                      showConfidence={true}
                                      confidenceMap={confidenceMap}
                                      evidenceMap={results.results.diagnosis_evidence_map}
                                    />
                                    {/* Display the final, curated clinical guidance for the optimal codes */}
                                    <ClinicalGuidanceDisplay guidance={results.results.optimal_icd10_codes_guidance} />
                                    
                                    {/* Further Research Section */}
                                    {results.results.optimal_icd10_codes.further_research && results.results.optimal_icd10_codes.further_research !== "No further research suggested." && (
                                      <Box>
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                                          Further Research:
                                        </Text>
                                        <Box p={3} bg={actionBg} borderRadius="md" border="1px solid" borderColor="green.200">
                                          <ReactMarkdown>{results.results.optimal_icd10_codes.further_research}</ReactMarkdown>
                                        </Box>
                                      </Box>
                                    )}
                                  </VStack>
                                </CardBody>
                              </Card>
                            ) : (
                              // Final fallback if nothing is available
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Optimal ICD-10 Recommendations</Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <Text color="gray.500" fontStyle="italic">
                                    No optimal coding recommendations were generated for this analysis.
                                  </Text>
                                </CardBody>
                              </Card>
                            )}

                            {/* ICD10 Comparison - Side by Side */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                              {/* Original ICD-10 Codes */}
                              {results.results?.input_codes?.icd10_codes && (
                                <Card bg={cardBg} borderColor={borderColor}>
                                  <CardHeader p={3}>
                                    <Heading size="sm">Original ICD-10 Codes</Heading>
                                  </CardHeader>
                                  <CardBody p={4} pt={0}>
                                    <Icd10CodesTable icd10Codes={results.results.input_codes.icd10_codes} />
                                  </CardBody>
                                </Card>
                              )}

                              {/* Best Case Recommendations */}
                              {results.results?.icd10_best_case && results.results.icd10_best_case.length > 0 && (
                                <Card bg={cardBg} borderColor={borderColor}>
                                  <CardHeader p={3}>
                                    <Heading size="sm">Best-Case Recommendations</Heading>
                                  </CardHeader>
                                  <CardBody p={4} pt={0}>
                                    <Icd10RecommendationsTable recommendations={results.results.icd10_best_case} showCptCoverage={true} />
                                    <ClinicalGuidanceDisplay guidance={results.results.icd10_best_case_guidance} />
                                  </CardBody>
                                </Card>
                              )}
                            </SimpleGrid>

                            {/* Analysis of Submitted Codes (Moved) */}
                            {results.results?.icd10_recommendations && results.results.icd10_recommendations.length > 0 && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Analysis of Submitted Codes</Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <Icd10RecommendationsTable recommendations={results.results.icd10_recommendations} />
                                  <ClinicalGuidanceDisplay guidance={results.results.icd10_recommendations_guidance} />
                                </CardBody>
                              </Card>
                            )}



                            {/* CPT Medical Necessity Support */}
                            {results.results?.cpt_medical_necessity_support && Object.keys(results.results.cpt_medical_necessity_support).length > 0 && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">CPT Medical Necessity Support</Heading>
                                  <Text fontSize="sm" color="gray.600" mt={1}>
                                    Medicare coverage for recommended diagnosis codes by CPT procedure
                                  </Text>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <VStack spacing={4} align="stretch">
                                    {Object.entries(results.results.cpt_medical_necessity_support).map(([cptCode, supportData]) => (
                                      <Box key={cptCode} p={4} borderWidth="1px" borderRadius="md" bg="white" borderColor={borderColor}>
                                        <VStack align="stretch" spacing={3}>
                                          {/* CPT Header */}
                                          <HStack justify="space-between" align="start">
                                            <VStack align="start" spacing={1}>
                                              <Text fontWeight="bold" fontSize="lg" color="blue.600">
                                                CPT {cptCode}
                                              </Text>
                                              <Badge 
                                                colorScheme={supportData.status === 'primary_contractor_coverage_found' ? 'green' : 'blue'} 
                                                fontSize="sm"
                                              >
                                                {getSourceStatusLabel(supportData.status)}
                                              </Badge>
                                            </VStack>
                                            <Badge colorScheme="gray" fontSize="sm">
                                              {supportData.covered_codes?.length || 0} Recommended Codes Covered
                                            </Badge>
                                          </HStack>

                                          {/* Supporting ICD10 Codes */}
                                          {supportData.covered_codes && supportData.covered_codes.length > 0 && (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                                                Medicare Coverage for Recommended Diagnosis Codes:
                                              </Text>
                                              <TableContainer>
                                                <Table variant="simple" size="sm">
                                                  <Thead>
                                                    <Tr>
                                                      <Th>ICD-10 Code</Th>
                                                      <Th>Description</Th>
                                                      <Th>CMS Article</Th>
                                                    </Tr>
                                                  </Thead>
                                                  <Tbody>
                                                    {supportData.covered_codes.map((icd10, index) => (
                                                      <Tr key={index}>
                                                        <Td fontFamily="mono" fontWeight="semibold">{icd10.code}</Td>
                                                        <Td>{icd10.description}</Td>
                                                        <Td>
                                                          {icd10.source_article ? (
                                                            <Link 
                                                              href={`https://www.cms.gov/medicare-coverage-database/view/article.aspx?articleId=${icd10.source_article.replace(/\D/g, '')}`} 
                                                              isExternal 
                                                              color="blue.600"
                                                            >
                                                              {icd10.source_article} <ExternalLinkIcon mx="2px" />
                                                            </Link>
                                                          ) : (
                                                            'N/A'
                                                          )}
                                                        </Td>
                                                      </Tr>
                                                    ))}
                                                  </Tbody>
                                                </Table>
                                              </TableContainer>
                                            </Box>
                                          )}
                                        </VStack>
                                      </Box>
                                    ))}
                                  </VStack>
                                </CardBody>
                              </Card>
                            )}

                            {/* Quick Assessment */}
                            {results.results?.approval_assessment && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Quick Assessment</Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <Text>{results.results.approval_assessment}</Text>
                                </CardBody>
                              </Card>
                            )}

                            {/* Detailed Analysis - Collapsible */}
                            <Card bg={cardBg} borderColor={borderColor}>
                              <CardHeader p={3}>
                                <Button
                                  variant="ghost"
                                  onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                                  rightIcon={<Icon as={showDetailedAnalysis ? ChevronUpIcon : ChevronDownIcon} />}
                                  size="sm"
                                  width="100%"
                                  justifyContent="space-between"
                                >
                                  <Heading size="sm">Detailed Analysis</Heading>
                                </Button>
                              </CardHeader>
                              <CardBody p={4} pt={0}>
                                <VStack align="stretch" spacing={3}>
                                  {results.results?.medical_necessity_analysis && (
                                    <Box>
                                      <Text fontWeight="semibold" mb={2}>Medical Necessity Analysis:</Text>
                                      <Box p={3} bg={codeBlockBg} borderRadius="md">
                                        <ReactMarkdown>{results.results.medical_necessity_analysis}</ReactMarkdown>
                                      </Box>
                                    </Box>
                                  )}
                                  {usedDocuments.length > 0 && (
                                    <Box>
                                      <Text fontWeight="semibold" mb={2}>Supporting Evidence:</Text>
                                      <Box p={3} bg={codeBlockBg} borderRadius="md">
                                        {usedDocuments.map((doc, index) => (
                                          <Box key={index} mb={2}>
                                            <Text fontSize="sm">
                                              <Text as="span" fontWeight="semibold">
                                                {doc.type}:
                                              </Text>{' '}
                                              {doc.id ? (
                                                <Link color="blue.600" cursor="pointer" onClick={() => handleSourceClick(doc)}>
                                                  {doc.title}
                                                </Link>
                                              ) : (
                                                doc.title
                                              )}
                                            </Text>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </VStack>
                              </CardBody>
                            </Card>


                            {/* Diagnosis Suggestions - Show what we suggest after showing what we have */}
                            {results.results?.diagnosis_suggestions && (
                              <DiagnosisSuggestionsDisplay diagnosisSuggestions={results.results.diagnosis_suggestions} />
                            )}



                            {/* Input ICD-10 Codes - Show what we found first */}
                            {results.results?.input_codes?.icd10_codes && false && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Current ICD-10 Codes</Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <Icd10CodesTable icd10Codes={results.results.input_codes.icd10_codes} />
                                </CardBody>
                              </Card>
                            )}
                          </VStack>
                        </TabPanel>

                        {/* Tab 2: ICD-10 Registry */}
                        <TabPanel px={0} py={2}>
                          <Icd10RegistryDisplay 
                            registry={results.results?.consolidated_icd10_analysis} 
                            referencedPolicies={results.results?.referenced_policies}
                          />
                        </TabPanel>

                        {/* Tab 3: Diagnosis Evidence */}
                        <TabPanel px={0} py={2}>
                            <DiagnosisEvidenceViewer 
                                evidenceMap={results.results?.diagnosis_evidence_map} 
                                caseFiles={caseFiles}
                                optimalCodes={results.results?.optimal_icd10_codes}
                            />
                        </TabPanel>

                        {/* Tab 3: Coding Summary */}
                        <TabPanel px={0} py={2}>
                          <VStack spacing={6} align="stretch">
                            {codingSummary ? (
                              <>
                                {/* 3-Column View: Original | Rules | Adjusted */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                  <CardHeader p={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <Heading size="sm">CPT Coding Summary ({claimTypeLabels[claimType] || claimType})</Heading>
                                    <Button as={RouterLink} to="/coding-search" size="sm" colorScheme="blue" variant="outline" rightIcon={<ExternalLinkIcon />}>
                                      Coding Search
                                    </Button>
                                  </CardHeader>
                                  <CardBody p={4} pt={0}>
                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                      {/* Original Codes */}
                                      <Box>
                                        <Text fontWeight="bold" color="gray.600" mb={2} borderBottom="2px solid" borderColor="gray.200" pb={1}>Original Codes</Text>
                                        <VStack align="start" spacing={2}>
                                          {codingSummary.baseline_codes?.[claimType]?.cpt_codes && Object.keys(codingSummary.baseline_codes[claimType].cpt_codes).length > 0 ? (
                                            (Array.isArray(codingSummary.baseline_codes[claimType].cpt_codes) 
                                              ? codingSummary.baseline_codes[claimType].cpt_codes 
                                              : Object.values(codingSummary.baseline_codes[claimType].cpt_codes)
                                            ).map((code, idx) => (
                                              <HStack key={idx}>
                                                <Badge colorScheme="gray" fontSize="sm">{code.cpt_code || code.key}</Badge>
                                                {code.modifiers && (Array.isArray(code.modifiers) ? code.modifiers : (typeof code.modifiers === 'string' ? code.modifiers.split(',') : [code.modifiers])).map((mod, mIdx) => (
                                                  mod ? <Badge key={mIdx} colorScheme="orange" fontSize="xs">{typeof mod === 'string' ? mod.trim() : mod}</Badge> : null
                                                ))}
                                              </HStack>
                                            ))
                                          ) : (
                                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No original codes</Text>
                                          )}
                                        </VStack>
                                      </Box>

                                      {/* Rules Applied */}
                                      <Box>
                                        <Text fontWeight="bold" color="gray.600" mb={2} borderBottom="2px solid" borderColor="gray.200" pb={1}>Rules Applied</Text>
                                        <VStack align="start" spacing={1}>
                                          {codingSummary.adjusted_codes?.[claimType]?.adjustment_log?.length > 0 ? (
                                            [...new Set(codingSummary.adjusted_codes[claimType].adjustment_log.map(rule => typeof rule === 'string' ? rule : rule.type || JSON.stringify(rule)))].map((ruleText, rIdx) => (
                                              <Text key={rIdx} fontSize="xs" color="red.600" bg="red.50" p={1} borderRadius="sm" w="100%">
                                                • {ruleText}
                                              </Text>
                                            ))
                                          ) : (
                                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No rules applied</Text>
                                          )}
                                        </VStack>
                                      </Box>

                                      {/* Adjusted Codes */}
                                      <Box>
                                        <Text fontWeight="bold" color="gray.600" mb={2} borderBottom="2px solid" borderColor="gray.200" pb={1}>Adjusted Codes</Text>
                                        <VStack align="start" spacing={2}>
                                          {codingSummary.adjusted_codes?.[claimType]?.cpt_codes && Object.keys(codingSummary.adjusted_codes[claimType].cpt_codes).length > 0 ? (
                                            (Array.isArray(codingSummary.adjusted_codes[claimType].cpt_codes) 
                                              ? codingSummary.adjusted_codes[claimType].cpt_codes 
                                              : Object.values(codingSummary.adjusted_codes[claimType].cpt_codes)
                                            ).map((code, idx) => (
                                              <HStack key={idx}>
                                                <Badge colorScheme="green" fontSize="sm">{code.cpt_code || code.key}</Badge>
                                                {code.modifiers && (Array.isArray(code.modifiers) ? code.modifiers : (typeof code.modifiers === 'string' ? code.modifiers.split(',') : [code.modifiers])).map((mod, mIdx) => (
                                                  mod ? <Badge key={mIdx} colorScheme="orange" fontSize="xs">{typeof mod === 'string' ? mod.trim() : mod}</Badge> : null
                                                ))}
                                              </HStack>
                                            ))
                                          ) : (
                                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No adjusted codes</Text>
                                          )}
                                        </VStack>
                                      </Box>
                                    </SimpleGrid>
                                  </CardBody>
                                </Card>

                                {/* Coding Comparison Section */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                  {/* CPT Comparison */}
                                  <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader p={3}>
                                      <Heading size="sm">Compare CPT Results</Heading>
                                    </CardHeader>
                                    <CardBody p={4} pt={0}>
                                      <VStack spacing={4} align="stretch">
                                        <Text fontSize="sm" color="gray.600">
                                          Compare Revedy-generated CPT codes against the original codes submitted to the payer, if available.
                                        </Text>
                                        <Button
                                          colorScheme="purple"
                                          onClick={handleCompareCoding}
                                          isLoading={comparisonLoading}
                                          loadingText="Comparing..."
                                        >
                                          Run CPT Comparison
                                        </Button>

                                        {comparisonError && (
                                          <Alert status="error" borderRadius="md">
                                            <AlertIcon />
                                            <AlertDescription>{comparisonError}</AlertDescription>
                                          </Alert>
                                        )}

                                        {comparisonResults && (
                                          <Box mt={4}>
                                            <Heading size="xs" mb={2}>CPT Analysis</Heading>
                                            <Box
                                              p={4}
                                              bg={codeBlockBg}
                                              borderRadius="md"
                                              border="1px solid"
                                              borderColor={borderColor}
                                              whiteSpace="pre-wrap"
                                              maxHeight="400px"
                                              overflowY="auto"
                                            >
                                              <ReactMarkdown>{comparisonResults}</ReactMarkdown>
                                            </Box>
                                          </Box>
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>

                                  {/* ICD-10 Comparison */}
                                  <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader p={3}>
                                      <Heading size="sm">Compare ICD-10 Results</Heading>
                                    </CardHeader>
                                    <CardBody p={4} pt={0}>
                                      <VStack spacing={4} align="stretch">
                                        <Text fontSize="sm" color="gray.600">
                                            Compare Revedy-generated ICD-10 codes against the original codes submitted to the payer, if available.
                                        </Text>
                                        <Button
                                          colorScheme="teal"
                                          onClick={handleCompareICD10}
                                          isLoading={icd10ComparisonLoading}
                                          loadingText="Comparing..."
                                        >
                                          Run ICD-10 Comparison
                                        </Button>

                                        {icd10ComparisonError && (
                                          <Alert status="error" borderRadius="md">
                                            <AlertIcon />
                                            <AlertDescription>{icd10ComparisonError}</AlertDescription>
                                          </Alert>
                                        )}

                                        {icd10ComparisonResults && (
                                          <Box mt={4}>
                                            <Heading size="xs" mb={2}>ICD-10 Analysis</Heading>
                                            <Box
                                              p={4}
                                              bg={codeBlockBg}
                                              borderRadius="md"
                                              border="1px solid"
                                              borderColor={borderColor}
                                              whiteSpace="pre-wrap"
                                              maxHeight="400px"
                                              overflowY="auto"
                                            >
                                              <ReactMarkdown>{icd10ComparisonResults}</ReactMarkdown>
                                            </Box>
                                          </Box>
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                </SimpleGrid>

                                <Divider />

                                {/* Adjusted Codes Section */}
                                {codingSummary.adjusted_codes?.[claimType] ? (
                                  <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader p={3}>
                                      <Heading size="sm">
                                        Final Adjusted Codes ({claimType === 'pro' ? 'Professional Fee' : 'Technical Fee'})
                                      </Heading>
                                    </CardHeader>
                                    <CardBody p={4} pt={0}>
                                      <CptCodesDisplay cptCodes={codingSummary.adjusted_codes[claimType].cpt_codes} />
                                    </CardBody>
                                  </Card>
                                ) : (
                                  <Text color="gray.500" fontStyle="italic">
                                    No adjusted codes found for the selected claim type.
                                  </Text>
                                )}

                                <Divider />

                                {/* Baseline Codes Section */}
                                {codingSummary.baseline_codes?.[claimType] ? (
                                  <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader p={3}>
                                      <Heading size="sm">
                                        Original Baseline Codes ({claimType === 'pro' ? 'Professional Fee' : 'Technical Fee'})
                                      </Heading>
                                    </CardHeader>
                                    <CardBody p={4} pt={0}>
                                      <CptCodesDisplay cptCodes={codingSummary.baseline_codes[claimType].cpt_codes} />
                                    </CardBody>
                                  </Card>
                                ) : (
                                  <Text color="gray.500" fontStyle="italic">
                                    No original baseline codes found for the selected claim type.
                                  </Text>
                                )}

                                <Divider />

                                {/* Impact Analysis Section */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                  <CardHeader p={3}>
                                    <Heading size="sm">Financial Impact Analysis</Heading>
                                  </CardHeader>
                                  <CardBody p={4} pt={0}>
                                    <VStack spacing={4} align="stretch">
                                      <Text fontSize="sm" color="gray.600">
                                        Generate a comprehensive financial and clinical variance analysis comparing the prospect's original coding and payment against Revedy's optimized recommendations.
                                      </Text>
                                      <Button
                                        colorScheme="blue"
                                        onClick={handleGenerateImpactAnalysis}
                                        isLoading={impactAnalysisLoading}
                                        loadingText="Analyzing Impact..."
                                        alignSelf="flex-start"
                                      >
                                        Generate Impact Analysis
                                      </Button>

                                      {impactAnalysisError && (
                                        <Alert status="error" borderRadius="md">
                                          <AlertIcon />
                                          <AlertDescription>{impactAnalysisError}</AlertDescription>
                                        </Alert>
                                      )}

                                      {impactAnalysisResult && (
                                        <Box mt={4}>
                                          <Heading size="xs" mb={2}>Analysis Results (JSON)</Heading>
                                          <Box
                                            p={4}
                                            bg={codeBlockBg}
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor={borderColor}
                                            whiteSpace="pre-wrap"
                                            fontFamily="monospace"
                                            fontSize="sm"
                                            maxHeight="500px"
                                            overflowY="auto"
                                          >
                                            {JSON.stringify(impactAnalysisResult, null, 2)}
                                          </Box>
                                        </Box>
                                      )}
                                    </VStack>
                                  </CardBody>
                                </Card>
                              </>
                            ) : (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardBody>
                                  <Text color="gray.500" fontStyle="italic">
                                    Coding summary is available after running analysis. If you have run an analysis and this is blank, there may be no adjustments for this payer.
                                  </Text>
                                </CardBody>
                              </Card>
                            )}
                          </VStack>
                        </TabPanel>

                        {/* Tab 3: CMS 1500 */}
                        <TabPanel px={0} py={2}>
                          <AdjustedClaimView 
                            results={results} 
                            cptDataSource={codingSummary?.adjusted_codes?.[claimType]?.cpt_codes}
                            claimType={claimType}
                            usedDocuments={usedDocuments}
                            handleSourceClick={handleSourceClick}
                          />
                          <CMS1500SequencingDisplay cmsData={results.results?.cms1500_claim_data} />
                        </TabPanel>

                        {/* Conditionally rendered NCCI Validated CMS 1500 Tab */}
                        {codingSummary?.adjusted_codes?.[`${claimType}_ncci`] && (
                          <TabPanel px={0} py={2}>
                            <VStack spacing={6} align="stretch">
                              <Heading size="md">CMS 1500 Form View (NCCI Validated)</Heading>
                              <AdjustedClaimView 
                                results={results} 
                                cptDataSource={codingSummary.adjusted_codes[`${claimType}_ncci`].cpt_codes}
                                claimType={claimType}
                                usedDocuments={usedDocuments}
                                handleSourceClick={handleSourceClick}
                              />

                              <Divider my={4} />

                              {/* Before NCCI Validation */}
                              {codingSummary.adjusted_codes?.[claimType] ? (
                                <Card bg={cardBg} borderColor={borderColor}>
                                  <CardHeader p={3}>
                                    <Heading size="sm">
                                      Before NCCI Validation (Carrier Adjusted)
                                    </Heading>
                                  </CardHeader>
                                  <CardBody p={4} pt={0}>
                                    <CptCodesDisplay cptCodes={codingSummary.adjusted_codes[claimType].cpt_codes} />
                                  </CardBody>
                                </Card>
                              ) : (
                                <Text color="gray.500" fontStyle="italic">
                                  No standard adjusted codes found for comparison.
                                </Text>
                              )}

                              <Divider />

                              {/* After NCCI Validation */}
                              <Card bg={cardBg} borderColor={borderColor} borderWidth="2px" borderColor="green.300">
                                <CardHeader p={3}>
                                  <Heading size="sm">
                                    After NCCI Validation
                                  </Heading>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <CptCodesDisplay cptCodes={codingSummary.adjusted_codes[`${claimType}_ncci`].cpt_codes} />
                                </CardBody>
                              </Card>
                            </VStack>
                          </TabPanel>
                        )}

                        {/* Payer Response Tab */}
                        <TabPanel px={0} py={2}>
                          <PayerResponseTabContent />
                        </TabPanel>

                        {/* Case Documents Tab */}
                        <TabPanel px={0} py={2}>
                          <CaseDocumentsTabContent 
                            files={caseFiles}
                            handleFileClick={handleFileClick}
                            formatFileSize={formatFileSize}
                            formatDate={formatDate}
                            handleFileView={handleFileView}
                            handleFileDownload={handleFileDownload}
                            fileLoading={fileLoading}
                            availableClaimTypes={availableClaimTypes}
                            handleAssignClaim={handleAssignClaim}
                          />
                        </TabPanel>

                        {/* ICD-10 Sandbox Tab */}
                        <TabPanel px={0} py={2}>
                          <Icd10SandboxTabContent 
                            results={results} 
                            caseId={caseId} 
                            accessToken={accessToken} 
                          />
                        </TabPanel>

                        {/* Chat Assistant Tab */}
                        <TabPanel px={0} py={2}>
                          <Card bg={cardBg} borderColor={borderColor}>
                            <CardHeader p={3}>
                              <Heading size="md">Coding Assistant</Heading>
                              <Text fontSize="sm" color="gray.600">
                                Ask questions about the case, coding decisions, and payer adjustments.
                              </Text>
                            </CardHeader>
                            <CardBody p={0}>
                              <ModernChat 
                                caseId={caseId} 
                                contextType="medical_necessity" 
                                height="600px"
                                collapsedHeight="250px"
                                additionalContext={results ? JSON.stringify(results.results, null, 2) : null}
                              />
                            </CardBody>
                          </Card>
                        </TabPanel>

                        {/* Pipeline Log Tab */}
                        <TabPanel px={0} py={2}>
                          <VStack spacing={4} align="stretch">
                            {/* Medical Necessity Search Log (Audit Trail) */}
                            {results.results?.search_log && results.results.search_log.length > 0 && (
                              <Card bg={cardBg} borderColor={borderColor}>
                                <CardHeader p={3}>
                                  <Heading size="sm">Medical Necessity Decision Trail</Heading>
                                  <Text fontSize="sm" color="gray.600">
                                    Step-by-step record of contractor selection, document search, and coverage mapping.
                                  </Text>
                                </CardHeader>
                                <CardBody p={4} pt={0}>
                                  <VStack spacing={4} align="stretch">
                                    {results.results.search_log.map((logEntry, index) => (
                                      <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="white" borderColor={borderColor}>
                                        <HStack justify="space-between" mb={2}>
                                          <Badge colorScheme="blue">{logEntry.step}</Badge>
                                          {logEntry.timestamp && (
                                            <Text fontSize="xs" color="gray.500">
                                              {new Date(logEntry.timestamp).toLocaleTimeString()}
                                            </Text>
                                          )}
                                        </HStack>
                                        <JsonRenderer data={logEntry} />
                                      </Box>
                                    ))}
                                  </VStack>
                                </CardBody>
                              </Card>
                            )}

                            <Card bg={cardBg} borderColor={borderColor}>
                              <CardBody>
                                <Button
                                  onClick={fetchPipelineLog}
                                  isLoading={logLoading}
                                  loadingText="Loading Log..."
                                  colorScheme="gray"
                                >
                                  Load Latest Pipeline Log
                                </Button>
                                {logError && (
                                  <Alert status="error" mt={4} borderRadius="md">
                                    <AlertIcon />
                                    <AlertDescription>{logError}</AlertDescription>
                                  </Alert>
                                )}
                              </CardBody>
                            </Card>
                            {pipelineLog && (
                              <VStack spacing={4} align="stretch">
                                {pipelineLog.map((logEntry) => (
                                  <Card key={logEntry.id} bg={cardBg} borderColor={borderColor}>
                                    <CardHeader>
                                      <HStack justify="space-between">
                                        <Heading size="sm">
                                          Step {logEntry.step_order}: {logEntry.step_name}
                                        </Heading>
                                        <Badge colorScheme={logEntry.step_type === 'LLMTask' ? 'purple' : 'blue'}>
                                          {logEntry.step_type}
                                        </Badge>
                                      </HStack>
                                    </CardHeader>
                                    <CardBody>
                                      <JsonRenderer data={logEntry.payload} />
                                    </CardBody>
                                  </Card>
                                ))}
                              </VStack>
                            )}
                          </VStack>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </VStack>
                ) : (
                  <Box
                    as="pre"
                    whiteSpace="pre-wrap"
                    fontFamily="monospace"
                    fontSize="sm"
                    p={4}
                    bg={codeBlockBg}
                    borderRadius="md"
                    overflow="auto"
                    maxHeight="600px"
                    border="1px solid"
                    borderColor={borderColor}
                  >
                    {JSON.stringify(results, null, 2)}
                  </Box>
                )}
              </CardBody>
            </Card>
          )}

          {/* Instructions - only show when no results and not loading */}
          {!results && !loading && !error && (
            <Card bg={instructionsBg} borderColor="blue.200">
              <CardBody p={4}>
                <VStack spacing={2} align="start">
                  <Heading size="sm">Medical Necessity Analysis:</Heading>
                  {!hasExistingResults && (
                    <Text fontSize="sm" color="gray.600" fontStyle="italic" mb={2}>
                      No stored results found for this case and claim type.
                    </Text>
                  )}
                  <Text fontSize="sm">
                    1. Select the claim type you want to analyze (Professional or Technical)
                  </Text>
                  <Text fontSize="sm">
                    2. Click "{hasExistingResults ? 'Re-run Analysis' : 'Analyze Medical Necessity'}" to run the AI-powered analysis
                  </Text>
                  <Text fontSize="sm">
                    3. The analysis uses existing adjusted CPT codes and automatically detects payer/carrier information
                  </Text>
                  <Text fontSize="sm">
                    4. Results include medical necessity assessment, approval likelihood, and recommendations
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Note:</strong> This requires that CPT codes have already been generated and adjusted for this case.
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Current Case ID:</strong> {caseId}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      {/* File Content Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={2} mt={-1}>
              <TagLabel>{selectedFile?.file_type}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={codeBlockBg}
              maxHeight="70vh" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedFile?.file_text || "No content available"}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedFile ? formatFileSize(selectedFile.file_size) : ""} • 
              {selectedFile ? " Uploaded " + formatDate(selectedFile.upload_date) : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Source Document Content Modal */}
      <Modal isOpen={isSourceOpen} onClose={onSourceClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>{selectedSource?.title || "Source Document"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={codeBlockBg}
              maxHeight="70vh" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {sourceLoading ? <Spinner /> : (sourceContent || "No content available")}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onSourceClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* JSON Report Modal */}
      <Modal isOpen={jsonModalOpen} onClose={() => setJsonModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>JSON Report Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={codeBlockBg}
              maxHeight="70vh" 
              overflowY="auto"
              fontFamily="monospace"
              fontSize="sm"
            >
              <pre>{JSON.stringify(jsonContent, null, 2)}</pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="green" 
              mr={3} 
              leftIcon={<DownloadIcon />}
              onClick={() => {
                const jsonString = JSON.stringify(jsonContent, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `medical_necessity_report_${claimType}_${caseId}.json`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                }, 100);
              }}
            >
              Download JSON
            </Button>
            <Button variant="ghost" onClick={() => setJsonModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AnalyzeCaseMN;
